import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, ViewEncapsulation, DestroyRef, inject, OnDestroy, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { DateTime as LuxonDateTime } from 'luxon';
import { Client } from 'src/providers/client/client';
import { Communication } from 'src/providers/communication/communication';
import { ConciergeApiService } from 'src/shared/services/concierge-api.service';
import { NotificationsQueriesService } from 'src/shared/services/notifications-queries.service';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { Dashboard } from 'src/providers/dashboard/dashboard';
import { Events } from 'src/providers/events/events';
import { Invoices } from 'src/providers/invoices/invoices';
import { WindowService } from 'src/shared/providers/window.service';
import { ReportIssue } from 'src/providers/report-issue/report-issue';
import { Loading } from 'src/shared/components/loading/loading';
import { DynamicComponentProvider } from 'src/shared/providers/dynamic-component/dynamic-component';
import { MapPopup } from './map-popup/map-popup';
import { Me } from 'src/providers/me/me';
import { popupOptions } from './map-popup/popup-opts';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { scrollContentWidth } from 'src/shared/components/scroll-wrapper/scroll-wrapper.component';
import { AddressModel, AddressJobSummary } from 'src/models/address.model';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { ParseDateTimePipe } from 'src/shared/pipes/parse-date.pipe';
import { Workflows } from 'src/providers/workflows/workflows';
import { AddressForm, FullAddressComponentsModel } from 'src/providers/address-form/address-form';
import { Addresses } from 'src/providers/customer/addresses';
import { GoogleGeocode } from 'src/providers/google-geocode/google-geocode';
import { MultipleCountryService } from 'src/providers/addresses/multiple-country.service';
import { CurrentAddress } from 'src/providers/addresses/current-address';
import { AutoCompleteAddressModel } from 'src/models/autocomplete-address.model';
import { Auth } from 'src/providers/auth/auth';
import { OneSignalNotification } from 'src/shared/providers/one-signal-notification';

import { BookJob } from 'src/providers/book-job/book-job';
import { Pros } from 'src/providers/pros/pros';

import { BookService } from 'src/shared/services/book.service';
import { ReportSettingsPage } from 'src/pages/dashboard/report-settings/report-settings';
import { ConciergePage } from 'src/pages/concierge/concierge';
import { AutomaticBookingAddressPage } from 'src/pages/more/automatic-booking/automatic-booking-address/automatic-booking-address';
import { AddPropertyFormComponent } from 'src/shared/components/add-property-form/add-property-form.component';
import { PlansPage } from 'src/pages/plans/plans';
import { SelectCategoryPage } from 'src/pages/select-category/select-category';
import { SelectPropertyPage } from 'src/pages/select-property/select-property';
import { UsersPage } from 'src/pages/users/users';
import { TranslationPipe } from 'src/shared/pipes/translation.pipe';
import { Schedule } from 'src/providers/schedule/schedule';
import { Job } from 'src/models/schedule-weekly-view.model';
import { PastJobPage } from '../more/past-job/past-job';
import { PastPrivateJobPage } from '../more/past-private-job/past-private-job';
import { JobPage } from '../schedule/job/job';
import { lastValueFrom, Subscription, take } from 'rxjs';
import { ModalController } from '@ionic/angular';
import { NotificationCenterPage } from '../notification-center/notification-center';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { PropertyInfo, PulseEvent } from 'src/shared/components/pulse-timeline/pulse-timeline.component';
import { AuthModalService } from 'src/shared/services/auth-modal.service';
import { PartnerSignupModalService } from 'src/shared/services/partner-signup-modal.service';
import { RoomScanService } from 'src/providers/room-scan/room-scan';
import { Genome } from 'src/providers/genome/genome';
import { propertyDashboardPath } from 'src/providers/guard/rental-routes.guard';
import { LocationDataService } from 'src/shared/services/location-data.service';
import { ThemeService } from 'src/shared/services/theme.service';
import { UrlStateService } from 'src/shared/services/url-state.service';
import { LocationDataResponse, PropertyDataResponse, PropertyDataUpdate, PropertyCodesResponse } from 'src/models/location-data.model';
import { AutocompleteComponent } from 'src/tidy-ui-components/components/form/auto-complete/auto-complete.component';
import { DropdownMenuItem } from 'src/shared/components/dropdown-menu/dropdown-menu.component';
import { SelectModel } from 'src/tidy-ui-components/components/form/select/select.model';
import { OnboardingDummyDataService } from 'src/shared/services/onboarding-dummy-data.service';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { AddressListing } from 'src/shared/services/marketing-channels.service';
import { formatPlatformName } from 'src/shared/constants/listing-platforms';
import { PropertyListingsPage } from 'src/pages/property-listings/property-listings.page';
import { FeatureGateService } from 'src/shared/services/feature-gate.service';
declare const mapboxgl: any;
declare const _google: any;
import { EditPropertyPage } from 'src/pages/edit-property/edit-property';
import Supercluster from 'supercluster';

@Component({
    selector: 'dashboard-page',
    templateUrl: './dashboard.html',
    styleUrls: ['./dashboard.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})

export class DashboardPage implements OnInit, OnDestroy {
  private destroyRef = inject(DestroyRef);

  addressIds = [];
  addresses: AddressModel[];
  conciergeItems: any;
  currentRows: any;
  clientName: string | null;
  errorMessage: string;
  hasPrivatePro: boolean;
  headers: any;
  houseIconIndex: number;
  pendingInvoices = [];
  isInvoicesLoaded: boolean;
  private houseIconCache = new Map<number, string>();
  private automaticBookingCache = new Map<number, string>();
  isRentalClient: boolean;
  isCompanyClient: boolean;
  clientInfo: any;

  // Hidden easter egg for capacitor test page
  private dashboardTitleClickCount = 0;
  private dashboardTitleClickTimeout: any;
  @ViewChild('map', {static: false}) mapElement: ElementRef;
  @ViewChild('addressAutoComplete') addressAutoComplete: AutocompleteComponent;
  messages: any;
  nudgeCards: any;
  routeReused: boolean;
  rows: any;
  report: any;
  reportId: string;
  standbyCount = 0;
  readonly desktopWidthContent: string = scrollContentWidth.LARGE;
  unresolvedLoggedIssues: any;
  mapInstance: any;
  isMapLoaded: boolean;
  isMessagesAndIssuesLoaded: boolean;
  isNudgeCardsLoaded: boolean;
  isTableLoaded: boolean;
  isConciergeItemsLoaded: boolean;
  loaded: boolean;
  showLoadingSpinner: boolean;
  language: string | null;
  fadeInContent = false;
  chatPrefillMessage = '';
  isAuthenticated = false;
  isPendingAuth = false;
  isGuestAccount = false;
  shownRowsCount = 10;
  public chartOptions: any;
  chartData: chartDataInterface | null;
  selectedPropertyId: number | null = null;
  selectedPropertyName = new TranslationPipe().transform('All Properties');
  propertyUpdatedSummary = '';
  propertyInfo: PropertyInfo = {
    name: new TranslationPipe().transform('All Properties'),
    summary: 'Monitoring all properties. Everything is running smoothly across your portfolio.',
    nextJob: 'Loading...',
    lastJob: 'Loading...',
    openIssues: 0,
    accessNotes: '',
    unpaidBills: 0,
    lowInventory: 0,
    maintenanceDue: 0,
    customFields: []
  };
  showPropertyDropdown = false;
  addPropertyForm: UntypedFormGroup;
  addressSuggestions: AutoCompleteAddressModel[] = [];
  addPropertyError = '';
  isAddingProperty = false;
  didSelectAddress = false;
  addressConfirmed = false;
  detectedLocationSummary = '';
  nextJobData: AddressJobSummary | null = null;
  lastJobData: AddressJobSummary | null = null;
  nextJobCard: any = null;
  lastJobCard: any = null;
  pulseEvents: PulseEvent[] = [];
  pulseEventsForMobile: PulseEvent[] = [];
  shouldShowEmptyState = false;
  showEmptyTimeline = false;
  weatherMarker: any;
  propertyViewMode: 'map' | 'floorplan' = 'map';
  showViewModeDropdown = false;
  isNativeIOS = false;
  propertyFloorplan: string | null = null;
  isPropertyZoomedOut = false;
  locationData: LocationDataResponse | null = null;
  propertyData: PropertyDataResponse | null = null;
  propertyCodesData: PropertyCodesResponse | null = null;
  isPropertyInfoExpanded = true;
  isLoadingPropertyData = false;
  pendingPropertyId: number | null = null;
  isLoadingLocationData = false;
  isLoadingPropertyCodes = false;
  isLoadingTimelineData = false;
  isLoadingPropertySummary = true;
  isTimelineDataLoaded = false;
  addressMarkers: any[] = [];
  private supercluster: Supercluster;
  private addressLocationsData: any[] = [];
  private mapMoveHandler: () => void;
  selectedBuildingFeatureIds: any[] = [];
  viewModeItems = [
    { value: 'map', viewValue: new TranslationPipe().transform('Map'), icon: 'map' },
    { value: 'floorplan', viewValue: new TranslationPipe().transform('Floorplan'), icon: 'grid_view' }
  ];

  isKeyboardVisible = false;
  componentSubscriptions: Subscription[] = [];

  // This page is Properties (/properties) for rental accounts and Overview (/overview) for the rest.
  get dashboardPath(): string {
    return propertyDashboardPath();
  }

  get notificationCount(): number {
    return this.notificationsQueries.notificationCount();
  }

  get propertyItems(): DropdownMenuItem[] {
    const items: DropdownMenuItem[] = [
      { value: null, label: new TranslationPipe().transform('All Properties') }
    ];
    if (this.addresses) {
      this.addresses.forEach(address => {
        items.push({
          value: address.id,
          label: address.address_name || address.address1
        });
      });
    }
    return items;
  }

  get selectedAddress(): AddressModel | undefined {
    if (this.selectedPropertyId === null || !this.addresses) {
      return undefined;
    }
    return this.addresses.find(addr => addr.id === this.selectedPropertyId);
  }

  mobileHeaderSelectValue = 'null';

  // Account type switcher for guest users
  accountTypeSelectValue = 'rental'; // Default to rental

  get accountTypeSelectItems(): SelectModel[] {
    return [
      { value: 'rental', viewValue: new TranslationPipe().transform('Property Manager'), icon: 'apartment' },
      { value: 'regular', viewValue: new TranslationPipe().transform('For Your Home'), icon: 'home' },
      { value: 'company', viewValue: new TranslationPipe().transform('For the Office'), icon: 'business' }
    ];
  }

  get shouldShowAccountTypeSwitcher(): boolean {
    const hasNoProperties = !this.addresses || this.addresses.length === 0;
    // Show switcher when user has no properties and has selected a guest type (or is unauthenticated)
    const hasGuestType = typeof localStorage !== 'undefined' && !!localStorage?.getItem('guest_view_type');
    return hasNoProperties && (this.isGuestAccount || hasGuestType || !this.isAuthenticated);
  }

  get accountTypeDropdownItems(): DropdownMenuItem[] {
    return [
      { value: 'rental', label: new TranslationPipe().transform('Property Manager') },
      { value: 'regular', label: new TranslationPipe().transform('For Your Home') },
      { value: 'company', label: new TranslationPipe().transform('For the Office') }
    ];
  }

  get selectedAccountTypeLabel(): string {
    const item = this.accountTypeDropdownItems.find(i => i.value === this.accountTypeSelectValue);
    return item?.label || 'Select Type';
  }

  get mobileHeaderSelectItems(): SelectModel[] {
    const items = this.propertyItems.map(item => ({
      value: item.value === null ? 'null' : String(item.value),
      viewValue: item.label
    }));
    items.push({ value: 'add-property', viewValue: `+ ${  new TranslationPipe().transform('Add Property')}` });
    return items;
  }

  onMobileHeaderItemSelected(value: string) {
    if (value === 'add-property') {
      this.goToAddProperty();
    } else if (value === 'null') {
      this.selectProperty(null);
    } else {
      this.selectProperty(Number(value));
    }
  }

  async onAccountTypeSelected(selectedType: string): Promise<void> {
    if (!selectedType) {
      return;
    }

    // Check if the type actually changed (compare with localStorage since ngModel updates first)
    const currentType = localStorage.getItem('guest_view_type') || localStorage.getItem('accountType');
    if (selectedType === currentType) {
      return;
    }

    try {
      // Set localStorage items first (matching account-type.ts behavior)
      localStorage.setItem('isRentalClient', selectedType === 'rental' ? 'true' : 'false');
      localStorage.setItem('accountType', selectedType);
      localStorage.setItem('guest_view_type', selectedType);

      // Save to backend
      await this.client.saveClientSettings({
        profile: { account_type: selectedType }
      });

      // Reload user data
      await this.me.load(true);

      // Hard refresh the page
      window.location.reload();
    } catch (error) {
      this.util.showError('Failed to update account type. Please try again.', 5000);
    }
  }

  private updateMobileHeaderSelectValue(): void {
    this.mobileHeaderSelectValue = this.selectedPropertyId === null ? 'null' : String(this.selectedPropertyId);
  }

  constructor(
    private addressesService: Addresses,
    private addressFormService: AddressForm,
    private authModalService: AuthModalService,
    private bookJobService: BookJob,
    private cdr: ChangeDetectorRef,
    private client: Client,
    public communication: Communication,
    private conciergeApi: ConciergeApiService,
    private currentAddress: CurrentAddress,
    private dashboard: Dashboard,
    private dynamicComponentService: DynamicComponentProvider,
    private events: Events,
    private fb: UntypedFormBuilder,
    private genome: Genome,
    private googleGeocode: GoogleGeocode,
    private invoices: Invoices,
    private me: Me,
    private modalController: ModalController,
    private multipleCountryService: MultipleCountryService,
    private navCtrl: CustomNavController,
    private notificationsQueries: NotificationsQueriesService,
    private onboardingDummyData: OnboardingDummyDataService,
    private prosProvider: Pros,
    private reportIssue: ReportIssue,
    private rightSidePanelService: RightSidePanelService,
    private route: ActivatedRoute,
    private schedule: Schedule,
    private storage: TidyStorage,
    private urlStateService: UrlStateService,
    private util: Util,
    public windowService: WindowService,
    private workflowsProvider: Workflows,
    private roomScanService: RoomScanService,
    private locationDataService: LocationDataService,
    private auth: Auth,
    private oneSignalNotification: OneSignalNotification,
    private partnerSignupModalService: PartnerSignupModalService,
    private themeService: ThemeService,
    private bookService: BookService,
    private demoBlock: DemoBlockService,
    private featureGate: FeatureGateService,
  ) {
    this.initAddPropertyForm();
    this.isNativeIOS = Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
    const savedAddressId = localStorage.getItem('dashboardAddressId');
    const savedAddressName = localStorage.getItem('dashboardAddressName');
    if (savedAddressId) {
      const addressId = Number(savedAddressId);
      if (!isNaN(addressId)) {
        this.pendingPropertyId = addressId;
        this.selectedPropertyId = addressId;
        this.mobileHeaderSelectValue = String(addressId);
        if (savedAddressName) {
          this.selectedPropertyName = savedAddressName;
        }
      }
    }
    effect(() => {
      const isDark = this.themeService.isDarkMode();
      if (this.mapInstance) {
        const style = isDark ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11';
        this.mapInstance.setStyle(style);
      }
    });
  }


  async ngOnInit() {
    if (Capacitor.isNativePlatform()) {
      Keyboard.addListener('keyboardWillShow', () => {
        this.isKeyboardVisible = true;
        this.cdr.detectChanges();
      });
      Keyboard.addListener('keyboardWillHide', () => {
        this.isKeyboardVisible = false;
        this.cdr.detectChanges();
      });
    }
    const didJustDeleteProperty = await this.storage.retrieve('didJustDeleteProperty');
    if (didJustDeleteProperty) {
      localStorage.removeItem('didJustDeleteProperty');
      localStorage.removeItem('dashboardAddressId');
      localStorage.removeItem('dashboardAddressName');
      this.selectedPropertyId = null;
      this.selectedPropertyName = new TranslationPipe().transform('All Properties');
      this.mobileHeaderSelectValue = 'null';
    }
    if (this.routeReused) {
      this.isLoadingPropertySummary = false;
    }
    const propertyDefaults = this.onboardingDummyData.getPropertyInfoDefaults();
    this.propertyInfo.name = propertyDefaults.name;
    this.propertyInfo.summary = propertyDefaults.summary;
    if (!this.selectedPropertyId) {
      this.selectedPropertyName = propertyDefaults.name;
    }
    this.isAuthenticated = this.auth.isAuthenticated();
    this.isPendingAuth = this.authModalService.isGuestCreationPending();
    this.isGuestAccount = await this.storage.retrieve('isGuestAccount') || false;
    const guestViewType = localStorage.getItem('guest_view_type') || localStorage.getItem('accountType');
    if (guestViewType) {
      this.accountTypeSelectValue = guestViewType;
    } else {
      // Detect type from flags if not in localStorage
      const isRental = localStorage.getItem('isRentalClient') === 'true';
      const isCompany = localStorage.getItem('isCompanyClient') === 'true';
      if (isCompany) {
        this.accountTypeSelectValue = 'company';
      } else if (isRental) {
        this.accountTypeSelectValue = 'rental';
      } else {
        this.accountTypeSelectValue = 'rental';
      }
    }
    const authCompleteSubscription = this.events.subscribe('auth:complete', () => {
      this.onAuthComplete();
    });
    this.componentSubscriptions.push(authCompleteSubscription);
    const authTypeSelectedSubscription = this.events.subscribe('auth:type-selected', () => {
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      this.isCompanyClient = localStorage.getItem('isCompanyClient') === 'true';
      this.isGuestAccount = true;
      const updatedGuestViewType = localStorage.getItem('guest_view_type') || localStorage.getItem('accountType');
      if (updatedGuestViewType) {
        this.accountTypeSelectValue = updatedGuestViewType;
      }
      this.cdr.detectChanges();
    });
    this.componentSubscriptions.push(authTypeSelectedSubscription);
    const addressUpdatedSubscription = this.events.subscribe('address:updated', (updatedAddress: any) => {
      if (!updatedAddress || this.selectedPropertyId !== updatedAddress.id) {
        return;
      }
      this.selectedPropertyName = updatedAddress.address_name || updatedAddress.address1 || '';
      this.propertyInfo = { ...this.propertyInfo, name: this.selectedPropertyName };
      this.storage.save(`dashboard_propertyInfo_${updatedAddress.id}`, this.propertyInfo);
      const property = this.addresses?.find(addr => addr.id === updatedAddress.id);
      if (property) {
        property.address_name = updatedAddress.address_name;
        this.storage.save('addresses', this.addresses);
      }
      this.cdr.detectChanges();
    });
    this.componentSubscriptions.push(addressUpdatedSubscription);

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const urlPropertyId = params['propertyId'] ? Number(params['propertyId']) : null;
      if (urlPropertyId && !isNaN(urlPropertyId)) {
        localStorage.setItem('addressId', urlPropertyId.toString());
        if (this.selectedPropertyId !== urlPropertyId) {
          if (this.loaded) {
            this.selectPropertyById(urlPropertyId);
          } else {
            this.pendingPropertyId = urlPropertyId;
          }
        }
      }
    });
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.isCompanyClient = localStorage.getItem('isCompanyClient') === 'true';
    this.fadeInContent = true;
    const chatPrefillMessage = localStorage.getItem('chatPrefillMessage');
    if (chatPrefillMessage) {
      localStorage.removeItem('chatPrefillMessage');
      this.chatPrefillMessage = chatPrefillMessage;
    }
    if (this.isAuthenticated) {
      const cachedAddresses = await this.storage.retrieve('addresses');
      if (cachedAddresses && cachedAddresses.length > 0) {
        this.addresses = cachedAddresses;
        this.shouldShowEmptyState = false;
      } else {
        this.shouldShowEmptyState = true;
      }
      await this.restoreCachedDashboardData();
    } else {
      this.shouldShowEmptyState = true;
    }
    this.buildAnonymousMap();
    try {
      if (!this.isAuthenticated) {
        const pendingPartnerCode = localStorage.getItem('pendingPartnerCode');
        if (pendingPartnerCode) {
          localStorage.removeItem('pendingPartnerCode');
          await this.partnerSignupModalService.openPartnerSignupModal(pendingPartnerCode);
        } else {
          const openSignIn = this.route.snapshot.data['openSignIn'];
          if (!this.authModalService.hasSelectedGuestType()) {
            if (openSignIn) {
              await this.authModalService.openAuthModal('auth', 'signin');
            } else {
              await this.authModalService.openGuestTypeModal();
            }
          }
        }
        this.makeMapBackgroundTransparent();
        this.loaded = true;
        this.addresses = [];
        //this.shouldShowEmptyState = true;
        await this.buildAnonymousMap();
        this.focusAddressInput();
        return;
      }
      await this.storage.save('isSelfBooking', false);
      this.makeMapBackgroundTransparent();

      this.loadPage();
    } catch (err) {
      this.errorMessage = err?.error?.message ?? err?.message;
    }
  }

  private async onAuthComplete() {
    this.isGuestAccount = await this.storage.retrieve('isGuestAccount') || false;
    if (this.isAuthenticated && !this.isPendingAuth) {
      return;
    }
    this.isPendingAuth = false;
    this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
    this.isCompanyClient = localStorage.getItem('isCompanyClient') === 'true';
    this.addresses = await this.client.getAddresses();
    this.shouldShowEmptyState = this.addresses?.length === 0;
    this.isAuthenticated = true;
    this.fadeInContent = true;
    this.loadPage();
    if (this.shouldShowEmptyState) {
      this.focusAddressInput();
    }
  }

  private makeMapBackgroundTransparent() {
    if (this.windowService?.isDesktopRes) {
      return;
    }
    setTimeout(() => {
      const ionContent = document.querySelector('app-scroll-wrapper ion-content') as HTMLElement;
      if (ionContent) {
        ionContent.style.setProperty('--background', 'transparent', 'important');
        ionContent.style.setProperty('--ion-background-color', 'transparent', 'important');
        ionContent.style.setProperty('--ion-color-tidy_background', 'transparent', 'important');
        ionContent.style.background = 'transparent';
        ionContent.style.backgroundColor = 'transparent';
      }
      const backgroundContent = ionContent?.shadowRoot?.querySelector('#background-content') as HTMLElement;
      if (backgroundContent) {
        backgroundContent.style.background = 'transparent';
        backgroundContent.style.backgroundColor = 'transparent';
      }
    }, 100);
  }

  async loadPage(showLoadingSpinner = false, setStoredRoute = true) {
    if (!this.auth.isAuthenticated()) {
      return;
    }
    this.isGuestAccount = await this.storage.retrieve('isGuestAccount') || false;
    this.houseIconCache.clear();
    this.automaticBookingCache.clear();
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }
    this.loaded = true;
    this.language = localStorage.getItem('language');
    if (!this.mapInstance) {
      this.isMapLoaded = false;
    }
    this.errorMessage = '';
    if (this.routeReused) {
      await Promise.all([
        this.loadUserInfo(),
        this.loadNudgeCards(),
        this.loadConciergeItems(),
        this.loadInvoices(),
        this.loadMessagesAndIssuesReports(),
        this.loadReports(),
        this.getInitialChart()
      ]);
    } else {
      this.loadUserInfo();
      this.loadNudgeCards();
      this.loadConciergeItems();
      this.loadInvoices();
      this.loadMessagesAndIssuesReports();
      this.loadReports();
      this.getInitialChart();
    }
    this.showLoadingSpinner = false;
    if (this.windowService.isDesktopRes && setStoredRoute) {
      this.storage.setStoredRoute(this.dashboardPath).then(async () => {
        this.routeReused = true;
        this.checkForPendingPropertySelection();
        await this.loadPage(true);
      });
    }
  }

  async loadReports(): Promise<void> {
    try {
      const reports = await this.dashboard.getDashboardSectionItems();
      const reportResponse = reports?.find((reportItem) => reportItem?.name === 'Overview');
      this.reportId = reportResponse?.id;
      localStorage.setItem('reportId', this.reportId);

      const addresses = await this.client.getAddresses();
      this.addresses = addresses;
      this.shouldShowEmptyState = this.addresses?.length === 0;

      if (this.reportId) {
        let report = await this.dashboard.getDashboardReportData(this.reportId);
        // Skip in the shared demo account: this normalization fires automatically on load,
        // so writing here would mutate the shared account and trip the demo write-block prompt.
        if (report?.settings?.sort_by_field_keys[0]?.field_key === '' && !this.demoBlock.isDemo) {
          const data = {
            sort_by_field_keys: [{
              field_key: 'address.address_1',
              sort: 'asc'
            }]
          };
          await this.dashboard.updateDashboardReport(data, this.reportId);
          report = await this.dashboard.getDashboardReportData(this.reportId);
        }
        this.report = report;
      }

      await this.loadTableAndBuildMap();
      if (this.addresses?.length === 0) {
        this.focusAddressInput();
      }
    } catch (error) {
      await this.buildMap();
      this.handleLoadErrors(error);
    }
  }

  async loadUserInfo(): Promise<void> {
    try {
      this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      if (this.isRentalClient === null) {
        await this.me.load();
        this.isRentalClient = localStorage.getItem('isRentalClient') === 'true';
      }
      this.clientInfo = await this.client.getClientInfo();
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  async loadTableAndBuildMap(): Promise<void> {
    try {
      await this.loadTable();
      await this.buildMap();
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  async loadMessagesAndIssuesReports(): Promise<void> {
    try {
      this.messages = await this.communication.getAllMessages();
      const loggedIssuesResponse = await this.reportIssue.getIssueReports();
      this.unresolvedLoggedIssues = loggedIssuesResponse.filter((item) => item?.status !== 'done');
      this.isMessagesAndIssuesLoaded = true;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  async loadNudgeCards(): Promise<void> {
    try {
      const nudgeCards = await this.dashboard.getNudgeCards();
      this.nudgeCards = await this.getNudgeCards(nudgeCards);
      this.hasPrivatePro = !nudgeCards?.includes('existing_pros');
      this.clientName = localStorage.getItem('customer_name');
      this.isNudgeCardsLoaded = true;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  async loadConciergeItems(): Promise<void> {
    try {
      const { body } = await lastValueFrom(this.conciergeApi.getConciergeItems());
      this.conciergeItems = body;
      this.isConciergeItemsLoaded = true;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  async loadInvoices(): Promise<void> {
    try {
      const invoices = await this.invoices.getPendingInvoices();
      this.parseInvoices(invoices);
      this.isInvoicesLoaded = true;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  handleLoadErrors(error: any): void {
    const route = window.location.pathname;
    if (!route.includes(this.dashboardPath)) {
      return;
    }
    const errorMessage = (error.error && error.error.message) ? error.error.message : error.message;
    this.util.showError(errorMessage, 10000);
  }

  parseInvoices(invoices) {
    this.pendingInvoices = [];
    invoices.map(invoice => {
      if (!invoice?.full_paid) {
        this.pendingInvoices.push(invoice);
      }
    });
  }

  parseReportFields(fields: any[]): any[] {
    fields.map((field) => {
      if (field.field_name === 'Next Job Date') {
        field.field_name = 'Next Job';
      }
      if (field.field_name === 'Last Completed Job Date') {
        field.field_name = 'Last Completed';
      }
      if (field.field_name === 'Maintenance Due Date') {
        field.field_name = 'Next Maintenance';
      }
    });
    return fields;
  }

  async loadTable() {
    try {
      if (!this.report?.settings) {
        return;
      }
      this.isTableLoaded = false;
      const hiddenFields = [
        'address.address_2',
        'address.address_name',
        'address.city',
        'address.state',
        'address.zipcode',
        'address.country',
        'next_job.start_time',
        'last_completed_job.start_time',
        'next_job.status',
        'last_completed_job.status',
        'last_completed_job.service_type',
        'next_job.service_type',
        'maintenances.name',
        'maintenances.date', // TODO: Remove this once we have a proper next maintenance date field
      ];

      this.report.settings.fields = this.parseReportFields(this.report.settings.fields);
      this.rows = await this.buildRows(hiddenFields);
      this.currentRows = this.rows.slice(0, 10);
      this.headers = this.report.settings.fields
        .filter(field => !hiddenFields.includes(field.field_key) && field.field_name && field.is_field_visible)
        .map(field => field.field_name);
      this.headers.push('last-column');

      this.isTableLoaded = true;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  buildRows(hiddenFields: string[]) {
    if (!this.report || !this.report?.settings) {
      return;
    }
    const indexOfAddressIdField = this.report.settings.fields.findIndex(
      (field) => field.field_key === 'address.id'
    );
    try {
      const rows = this.report.data.rows.map((row) => {
        const object: any = {};
        row.map((item, index) => {
          if (this.report.settings.fields?.[index]?.is_field_visible && !hiddenFields.includes(this.report.settings.fields[index].field_key)) {
            const fieldKey = this.report.settings.fields[index].field_key;
            object[this.report.settings.fields[index].field_name] = {
              value: this.getParsedRowName(item, row, index),
              action: this.addressActionCheck(
                fieldKey,
                indexOfAddressIdField,
                row
              ),
              addressId: row[indexOfAddressIdField]
            };
            if (this.report.settings.fields[index].field_name === 'Address') {
              object[this.report.settings.fields[index].field_name]['color'] = this.addresses?.find((address) => address.id === row[row?.length - 1])?.color;
            }
          }
        });
        object['last-column'] = {
          value: '',
          action: null
        };
        return object;
      });
      return rows;
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  getParsedRowName(item: any, row: any, index: number): string {
    const headers = this.report.data.header;
    const header = headers[index];
    if (header === 'Address') {
      return this.getParsedAddressName(row);
    }
    if (header === 'Next Job Date') {
      const nextJobType = row[this.getFieldIndex('next_job.service_type')];
      if (nextJobType) {
        return `${new TranslationPipe().transform(nextJobType)} (${this.getParsedRowDate(row, 'Next Job Date', 'Next Job Time')})`;
      }
      return this.getParsedRowDate(row, 'Next Job Date', 'Next Job Time');
    }
    if (header === 'Last Completed Job Date') {
      const lastCompletedJobType = row[this.getFieldIndex('last_completed_job.service_type')];
      if (lastCompletedJobType) {
        return `${new TranslationPipe().transform(lastCompletedJobType)} (${this.getParsedRowDate(row, 'Last Completed Job Date', 'Last Completed Job Time')})`;
      }
      return this.getParsedRowDate(row, 'Last Completed Job Date', 'Last Completed Job Time');
    }
    if (header === 'Maintenance Due Date') {
      const maintenanceName = row[this.getFieldIndex('maintenances.name')];
      if (maintenanceName) {
        return `${new TranslationPipe().transform(maintenanceName)} (${this.getParsedRowDate(row, 'Maintenance Due Date', 'Maintenance Due Time')})`;
      }
      return this.getParsedRowDate(row, 'Maintenance Due Date', 'Maintenance Due Time');
    }
    return item;
  }

  getParsedAddressName(row: any): string {
    const headers = this.report.data.header;
    const addressHeaderIndex = headers.findIndex((header) => header === 'Address');
    const unitHeaderIndex = headers.findIndex((header) => header === 'Unit');
    const addressNameHeaderIndex = headers.findIndex((header) => header === 'Address Name');
    const address = row[addressHeaderIndex];
    const unit = row[unitHeaderIndex];
    const addressName = row[addressNameHeaderIndex];
    return addressName ? addressName : `${address}${unit ? `, ${unit}` : ''}`;
  }

  getParsedRowDate(row: any, dateHeader: string, timeHeader: string): string {
    const headers = this.report.data.header;
    const dateHeaderIndex = headers.findIndex((header) => header === dateHeader);
    const timeHeaderIndex = headers.findIndex((header) => header === timeHeader);
    const date = row?.[dateHeaderIndex];
    const time = row?.[timeHeaderIndex];
    if (date && time) {
      const parsedDate = LuxonDateTime.fromFormat(date, 'MMM d, yyyy').toISO();
      return `${new ParseDateTimePipe().transform(parsedDate, 'MMM d')  } ${  new ParseDateTimePipe().transform(time, 'hh:mma')}`;
    }
    if (date && !time) {
      const parsedDate = LuxonDateTime.fromFormat(date, 'MMM d, yyyy').toISO();
      return new ParseDateTimePipe().transform(parsedDate, 'MMM d');
    }
    return '';
  }

  translateAMPM(dateTime) {
    return dateTime.replace('AM', new TranslationPipe().transform('am')).replace('PM', new TranslationPipe().transform('pm'));
  }

  checkIfAutomaticBookingEnabled(addressId): string {
    if (this.automaticBookingCache.has(addressId)) {
      return this.automaticBookingCache.get(addressId);
    }
    const address = this.addresses.find(addressItem => addressItem.id === addressId);
    const status = address?.address_automatic_booking_setting?.automatic_booking_enabled ? 'enabled' : 'disabled';
    this.automaticBookingCache.set(addressId, status);
    return status;
  }

  getAutomaticBookingDetail(addressId, field) {
    const address = this.addresses.find(addressItem => addressItem.id === addressId);
    if (field === 'service') {
      return address?.address_automatic_booking_setting?.required_team_service?.name;
    }
    if (field === 'earliest_time') {
      return address?.address_automatic_booking_setting?.booking_start_time_no_earlier_than;
    }
    if (field === 'end_time') {
      return address?.address_automatic_booking_setting?.booking_end_time_no_later_than;
    }
  }

  addressActionCheck(fieldKey, indexOfAddressId, row) {
    if (fieldKey === 'address.address_1' && indexOfAddressId !== -1 && row[indexOfAddressId]) {
      return this.goToAddress.bind(this, row[indexOfAddressId]);
    }
    if (fieldKey.includes('last_completed_job')) {
      const rowIndex = this.report.settings.fields.find((item) => item.field_key === 'last_completed_job.id')?.field_order - 1;
      const lastCompletedJobId = row[rowIndex];
      return () => this.getJobCardAndJobUrl(row[indexOfAddressId], lastCompletedJobId, true);
    }
    if (fieldKey.includes('next_job')) {
      const rowIndex = this.report.settings.fields.find((item) => item.field_key === 'next_job.id')?.field_order - 1;
      const nextJobId = row[rowIndex];
      return () => this.getJobCardAndJobUrl(row[indexOfAddressId], nextJobId, false);
    }
    if (fieldKey.includes('maintenances')) {
      return this.goToAddress.bind(this, row[indexOfAddressId]);
    }
    return null;
  }

  async getJobCardAndJobUrl(addressId, jobId, isPastJob) {
    if (!jobId) {
      return null;
    }
    const job = await this.schedule.getJobCard(addressId, jobId);
    this.goToCleaningDetail(job.template_data, addressId, isPastJob);
  }

  @Loading('', true)
  async goToCleaningDetail(cleaning: any, addressId: number, isPastJob: boolean) {
    try {
      const isMobileResolution = !this.windowService.isDesktopRes;
      const isPrivateJob = cleaning.isPrivate;
      cleaning.job.booking = cleaning.booking;
      if (!isMobileResolution) {
        this.navigateToJobPageInsideDialog(cleaning.job, addressId, isPastJob, isPrivateJob);
        return;
      }
      localStorage.setItem('pastJobBackPage', 'properties');
      if (isPastJob) {
        if (isPrivateJob) {
          this.navCtrl.navigateForward([`past-private-job/${cleaning.job.id}`]);
        } else {
          this.navCtrl.navigateForward([`past-job/${cleaning.job.id}`]);
        }
      } else {
        this.navCtrl.navigateForward([`job/${addressId}/${cleaning.job.id}`], { object: cleaning.job });
      }
    } catch (error) {
      this.handleLoadErrors(error);
    }
  }

  navigateToJobPageInsideDialog(cleaning: Job, addressId: number, isPastJob: boolean, isPrivateJob: boolean): void {
    const params = {
      jobId: cleaning.id,
      addressId,
      bookingId: cleaning.booking.id,
      ...cleaning,
    }
    this.storage.save('dialog-params', params);
    this.storage.save('dialog-right-side-open', true);
    if (isPastJob && isPrivateJob) {
      this.rightSidePanelService.openRightSidePanel(PastPrivateJobPage);
    } else if (isPastJob) {
      this.rightSidePanelService.openRightSidePanel(PastJobPage);
    } else {
      this.rightSidePanelService.openRightSidePanel(JobPage);
    }
  }

  goToEditAddressPage(rowIndex) {
    this.report?.data?.rows?.map((column, i) => {
      if (i === rowIndex) {
        const addressId = column[19];
        this.goToAddress(addressId);
      }
    });
  }

  goToAddress(addressId) {
    this.selectProperty(addressId);
  }

  async goToAddAddress() {
    this.navCtrl.navigateForward('add-property');
  }

  async goToIntegrations() {
    this.navCtrl.navigateForward('integration/all');
  }

  goToLogin() {
    this.authModalService.openAuthModal();
  }

  goToAutomaticBooking(addressId) {
    localStorage.setItem('automaticBookingAddressBackPage', 'properties');
    const params = {
      addressId
    }
    this.goToPage(`automatic-booking-property/${addressId}`, params, AutomaticBookingAddressPage);
  }

  async buildAnonymousMap() {
    try {
      if (!this.mapElement) {
        return setTimeout(() => this.buildAnonymousMap(), 100);
      }
      const location = await this.getLocationFromIP();
      this.mapInstance = this.createMap();
      this.mapInstance.setCenter([location.lng, location.lat]);
      this.mapInstance.setZoom(10);
      this.isMapLoaded = true;
    } catch (error) {
      if (this.mapInstance) {
        this.mapInstance.setCenter([-118.2437, 34.0522]);
        this.mapInstance.setZoom(10);
      }
    }
  }

  async getLocationFromIP(): Promise<{lat: number, lng: number}> {
    try {
      const res = await fetch('https://ipapi.co/json/');
      const data = await res.json();
      return { lat: data.latitude || 34.0522, lng: data.longitude || -118.2437 };
    } catch {
      return { lat: 34.0522, lng: -118.2437 };
    }
  }

  async buildMap(retryCount = 0) {
    try {
      if (!this.mapElement) {
        if (retryCount > 50) {
          return;
        }
        return setTimeout(() => this.buildMap(retryCount + 1), 50);
      }
      const events = await this.dashboard.getMapEvents();
      if (!this.mapInstance) {
        this.mapInstance = this.createMap();
      }
      const addressLocations = this.getAddressLocations();
      if (this.mapInstance && !addressLocations?.length) {
        if (!this.isMapLoaded) {
          this.mapInstance.setCenter([-118.2437, 34.0522]);
          this.mapInstance.setZoom(10);
        }
      }
      this.addAddressLocationsToMap(addressLocations, this.mapInstance);
      if (!addressLocations.length) {
        if (!this.isMapLoaded) {
          await this.extendBoundsToShowAllAddresses(this.mapInstance, events);
        }
        this.isMapLoaded = true;
        this.autoSelectSingleProperty();
        return;
      }
      this.setMapBounds(this.mapInstance, addressLocations, events);
      if (!this.pendingPropertyId) {
        this.isMapLoaded = true;
      }
      this.autoSelectSingleProperty();
    } catch (error) {
      this.handleLoadErrors(error);
      if (this.mapInstance && !this.isMapLoaded) {
        this.mapInstance.setCenter([-118.2437, 34.0522]);
        this.mapInstance.setZoom(10);
      }
    }
  }

  setMapBounds(map, addressLocations, events) {
    if (this.pendingPropertyId) {
      return;
    }
    if (this.selectedPropertyId === null) {
      if (this.addresses?.length === 1) {
        return;
      }
      this.zoomToAllProperties();
      return;
    }
    const bounds = new mapboxgl.LngLatBounds();
    addressLocations.forEach((location) => {
      bounds.extend([location.longitude, location.latitude]);
    });
    map.fitBounds(bounds, { padding: 50, duration: 0 });
  }

  private checkForPendingPropertySelection(): void {
    const savedAddressId = localStorage.getItem('dashboardAddressId');
    if (savedAddressId) {
      const addressId = Number(savedAddressId);
      if (!isNaN(addressId) && addressId !== this.selectedPropertyId) {
        this.pendingPropertyId = addressId;
        localStorage.removeItem('dashboardAddressId');
        localStorage.removeItem('dashboardAddressName');
      }
    }
  }

  private autoSelectSingleProperty() {
    if (!this.mapInstance) {
      return;
    }
    const hasPendingProperty = this.pendingPropertyId !== null;
    let propertyIdToSelect = this.pendingPropertyId || (this.addresses?.length === 1 ? this.addresses[0].id : null);
    if (propertyIdToSelect && this.addresses?.length > 0 && !this.addresses.find(a => a.id === propertyIdToSelect)) {
      propertyIdToSelect = null;
      localStorage.removeItem('dashboardAddressId');
    }
    if (!propertyIdToSelect) {
      if (this.addresses?.length > 0 && this.selectedPropertyId !== null && !this.selectedPropertyName) {
        this.selectedPropertyId = null;
        this.selectedPropertyName = new TranslationPipe().transform('All Properties');
      }
      return;
    }
    if (this.selectedPropertyId === propertyIdToSelect && this.selectedPropertyName) {
      const property = this.addresses?.find(addr => addr.id === propertyIdToSelect);
      if (property) {
        const actualPropertyName = property.address_name || property.address1 || '';
        this.selectedPropertyName = actualPropertyName;
        localStorage.setItem('dashboardAddressName', actualPropertyName);
        if (!this.routeReused) {
          this.isLoadingPropertySummary = true;
          this.propertyInfo = {
            ...this.propertyInfo,
            name: actualPropertyName,
            nextJob: 'Loading...',
            lastJob: 'Loading...',
          };
        } else {
          this.propertyInfo = { ...this.propertyInfo, name: actualPropertyName };
        }
        this.zoomToProperty(property);
        this.fetchLocationAndPropertyData(property);
        this.loadTimelineData(propertyIdToSelect);
        this.loadPropertySummary(propertyIdToSelect);
        this.loadGettingStartedStatus(propertyIdToSelect);
      }
      this.updateAddressMarkerOffsets();
      this.pendingPropertyId = null;
      return;
    }
    this.pendingPropertyId = null;
    if (hasPendingProperty) {
      this.selectProperty(propertyIdToSelect);
    } else {
      setTimeout(() => {
        if (this.selectedPropertyId === null) {
          this.selectProperty(propertyIdToSelect);
        }
      }, 100);
    }
  }

  async extendBoundsToShowAllAddresses(map, events) {
    try {
      const bounds = new mapboxgl.LngLatBounds();
      const addresses = await this.client.getAddresses();
      addresses.map((address) => {
        bounds.extend([address.longitude, address.latitude]);
      });
      events.map((event) => {
        bounds.extend([event.longitude, event.latitude]);
      });
      map.fitBounds(bounds, { padding: 50, duration: 0 });
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  createMap() {
    mapboxgl.accessToken = 'pk.eyJ1IjoidmljdG9yZmVycmF6IiwiYSI6ImNrdHVoNHc1bTIwYjYycHE1NmhjdzVtdzMifQ.OIqKrdlDh4fJH0g0MO1zLg';
    const map = new mapboxgl.Map({
      container: this.mapElement.nativeElement,
      style: this.themeService.isDarkMode() ? 'mapbox://styles/mapbox/dark-v11' : 'mapbox://styles/mapbox/light-v11',
      zoom: 8,
      maxZoom: 21,
      pitch: 60,
      bearing: -17.6,
      antialias: true
    });
    map.on('style.load', () => {
      this.add3DBuildingsLayer(map);
      this.setMapLanguage(map);
    });
    map.on('load', () => {
      setTimeout(() => {
        if (this.mapInstance === map) {
          map.resize();
        }
      }, 100);
    });
    return map;
  }

  getAddressLocations() {
    if (!this.report?.settings || !this.report?.data?.rows) {
      return [];
    }
    const latIdx = this.getFieldIndex('address.latitude');
    const lngIdx = this.getFieldIndex('address.longitude');
    const idIdx = this.getFieldIndex('address.id');
    const statusIdx = this.getFieldIndex('next_job.status');
    return this.report.data.rows.slice(0, 50).map(row => ({
      address: row[0],
      latitude: row[latIdx],
      longitude: row[lngIdx],
      addressId: row[idIdx],
      nextJobStatus: row[statusIdx]
    }));
  }

  getFieldIndex(fieldKey) {
    const field = this.report.settings.fields.find (fieldItem => fieldItem.field_key === fieldKey);
    return this.report.settings.fields.indexOf(field);
  }

  addAddressLocationsToMap(addressLocations, map) {
    this.addressMarkers.forEach(marker => marker.remove());
    this.addressMarkers = [];
    this.addressLocationsData = addressLocations;
    if (this.selectedPropertyId !== null) {
      this.renderMarkersWithoutClustering(addressLocations, map);
      return;
    }
    const points = addressLocations
      .filter(loc => loc.latitude && loc.longitude)
      .map((location, index) => ({
        type: 'Feature' as const,
        properties: {
          addressId: location.addressId,
          address: location.address,
          nextJobStatus: location.nextJobStatus,
          index
        },
        geometry: {
          type: 'Point' as const,
          coordinates: [parseFloat(location.longitude), parseFloat(location.latitude)]
        }
      }));
    this.supercluster = new Supercluster({
      radius: 40,
      maxZoom: 14,
      minPoints: 3
    });
    this.supercluster.load(points);
    if (this.mapMoveHandler) {
      map.off('moveend', this.mapMoveHandler);
    }
    this.mapMoveHandler = () => this.updateClusters(map);
    map.on('moveend', this.mapMoveHandler);
    this.updateClusters(map);
  }

  private updateClusters(map) {
    this.addressMarkers.forEach(marker => marker.remove());
    this.addressMarkers = [];
    if (!this.supercluster || !map) {return;}
    const bounds = map.getBounds();
    const zoom = Math.floor(map.getZoom());
    const clusters = this.supercluster.getClusters(
      [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()],
      zoom
    );
    clusters.forEach(cluster => {
      if (cluster.properties.cluster) {
        this.createClusterMarker(cluster, map);
      } else {
        this.createSingleMarker(cluster.properties, cluster.geometry.coordinates, map);
      }
    });
  }

  private createClusterMarker(cluster, map) {
    const [lng, lat] = cluster.geometry.coordinates;
    const count = cluster.properties.point_count;
    const el = document.createElement('div');
    let sizeClass = '';
    if (count >= 50) {
      sizeClass = ' xlarge';
    } else if (count >= 20) {
      sizeClass = ' large';
    }
    el.className = `cluster-marker${  sizeClass}`;
    el.innerHTML = `<span class="cluster-count">${count}</span>`;
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      const expansionZoom = this.supercluster.getClusterExpansionZoom(cluster.id);
      map.flyTo({ center: [lng, lat], zoom: Math.min(expansionZoom, 18) });
    });
    const marker = new mapboxgl.Marker(el);
    marker.setLngLat([lng, lat]).addTo(map);
    this.addressMarkers.push(marker);
  }

  private createSingleMarker(properties, coordinates, map) {
    const [lng, lat] = coordinates;
    const propertyName = (properties.address || '').split(',')[0].trim();
    const el = document.createElement('div');
    el.className = 'property-marker';
    el.innerHTML = `<div class="marker-content"><span class="marker-text">${propertyName}</span></div>`;
    if (properties.addressId) {
      el.style.cursor = 'pointer';
      el.addEventListener('click', (e) => {
        e.stopPropagation();
        this.selectProperty(properties.addressId);
      });
    }
    const marker = new mapboxgl.Marker(el);
    marker.setLngLat([lng, lat]).addTo(map);
    this.addressMarkers.push(marker);
  }

  private renderMarkersWithoutClustering(addressLocations, map) {
    addressLocations.forEach((location) => {
      const el = document.createElement('div');
      el.className = 'property-marker';
      const propertyName = (location.address || '').split(',')[0].trim();
      el.innerHTML = `<div class="marker-content"><span class="marker-text">${propertyName}</span></div>`;
      if (location.addressId) {
        el.style.cursor = 'pointer';
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          this.selectProperty(location.addressId);
        });
      }
      const labelOffset = this.getLabelOffset();
      const marker = new mapboxgl.Marker(el, {
        offset: [0, labelOffset],
        anchor: 'center'
      });
      marker.setLngLat([location.longitude, location.latitude]).addTo(map);
      this.addressMarkers.push(marker);
    });
  }

  private updateAddressMarkerOffsets() {
    const isViewingSingleProperty = this.selectedPropertyId !== null;
    const labelOffset = this.getLabelOffset();
    const markerOffset = isViewingSingleProperty ? [0, labelOffset] : [0, 0];
    this.addressMarkers.forEach((marker) => {
      marker.setOffset(markerOffset);
      const el = marker.getElement();
      if (isViewingSingleProperty) {
        el.classList.add('hidden-label');
      } else {
        el.classList.remove('hidden-label');
      }
    });
  }

  getHouseIcon(index): string {
    if (this.houseIconCache.has(index)) {
      return this.houseIconCache.get(index);
    }
    if (index === 10) {
      this.houseIconIndex = 1;
    } else {
      this.houseIconIndex = index;
    }
    const icon = this.dashboard.getHouseImage(this.houseIconIndex);
    this.houseIconCache.set(index, icon);
    return icon;
  }

  addEventsToMap(events, map) {
    events.forEach((event, i) => {
      const data = this.getEventData(event, i);
      if (data !== null) {
        const popupContent = this.dynamicComponentService.injectComponent(
          MapPopup, x => x.data = {
            image: data.popupImage,
            body: data?.body || '',
            header: data.header,
            learnMore: data.learnMore
          }
        );
        this.createPopupAndMarker(map, popupContent, data.mapIcon, event);
      }
    });
  }

  createPopupAndMarker(map, popupContent, mapIcon, location, isAddressLocation = false, customOpts = null) {
    const popup = new mapboxgl.Popup({...popupOptions, ...customOpts}).setDOMContent(popupContent);
    const _isJobOpportunity = true;
    if (location.nextJobStatus === 'job_request') {
      const lottiePlayerEl = document.createElement('div');
      lottiePlayerEl.innerHTML = `<lottie-player src="https://assets1.lottiefiles.com/packages/lf20_yw0d2riu.json"  background="transparent"  speed="0.5"  style="width: 150px; height: 150px;"  loop  autoplay></lottie-player>`;
      this.createMarkerInstance({map, el: lottiePlayerEl, longLat: [ location.longitude, location.latitude ]}, popup);
    }
    const el = document.createElement('div');
    el.className = 'marker';
    if (isAddressLocation) {
      el.innerHTML = `<image src="${mapIcon}" style="width: 40px; height: 40px; padding-top: 10px;"></image>`;
    } else {
      el.innerHTML = `<image src="${mapIcon}" style="width: 30px; height: 30px;"></image>`;
    }
    this.createMarkerInstance({map, el, longLat: [ location.longitude, location.latitude ]}, popup);
  }

  createMarkerInstance(obj, popup) {
    const {el, longLat, map} = obj;
    const marker = new mapboxgl.Marker(el);

    marker.getElement().addEventListener('click', () => {

      const flyToCoords = [...longLat];
      const getZoom = map.getZoom() ;
      const zoomOut = getZoom < 0;

      const lgnSum = zoomOut ? 200 / (5 * getZoom) : 50 / (2 ** getZoom);

      if (flyToCoords[1] + lgnSum > 90) {
        flyToCoords[1] = 90;
      } else if ((flyToCoords[1] + lgnSum < -90)) {
        flyToCoords[1] = -90;
      } else {
        flyToCoords[1] += lgnSum;
      }

      map.flyTo({
        center: flyToCoords
      })
    });

    marker.setLngLat(longLat).setPopup(popup).addTo(map);
  }

  getEventData(event, _i) {
    const categories = {
      'Regular Cleaning': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Pool Cleaning': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Carpet Cleaning': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Window Washing': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Lawn & Garden Care': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Junk Removal': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Pest Control': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Pressure Washing': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Tree Trimming': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Inspection': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Trash Service': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Handyman': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Laundry Service': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Locksmith': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Painting': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Grill Maintenance': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Biohazard Cleaning': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Plumbing': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      },
      'Appliance Repair': {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      }
    }
    if (event.event.name === 'samantha.job.created') {
      const category: any = categories[event.event.data.service_category] || {
        mapIcon: event.event.data.service_map_icon_url,
        popupImage: event.event.data.service_icon_url
      };
      category['body'] = 'Recently completed near you.';
      category['header'] = this.titleCase(event.event.data.service_name);
      category['learnMore'] = 'https://help.tidy.com/dashboard#nn-overview-report';
      return category;
    } else if (event.event.name === 'samantha.emergency_cleaning_request.standby.created') {
      this.standbyCount += 1;
      return {
        mapIcon: this.getStandbyImage(),
        body: 'This Pro is on standby to accept jobs in your area.',
        header: 'Standby',
        popupImage: 'assets/img/standby-popup.svg',
        learnMore: 'https://help.tidy.com/dashboard#nn-overview-report'
      };
    } else {
      return null;
    }
  }

  titleCase(name) {
    return name.split(' ')
       .map(w => w[0].toUpperCase() + w.substr(1).toLowerCase())
       .join(' ');
   }

  getStandbyImage() {
    if (this.standbyCount % 3 === 0) {
      return 'assets/img/car-up-left.svg';
    } else if (this.standbyCount % 2 === 0) {
      return 'assets/img/car-right.svg';
    } else {
      return 'assets/img/car-down.svg';
    }
  }

  goToReportSettings() {
    if (this.demoBlock.block()) { return; }
    localStorage.setItem('cameFromReportAdded', 'false');
    const params = {
      report: this.report
    }
    this.goToPage(`reports/report-settings/${this.report.id}`, params, ReportSettingsPage);
    this.rightSidePanelService.afterPanelClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.loadReports();
    });
  }

  goToNotificationCenter() {
    this.goToPage('notification-center', { backPage: 'properties' }, NotificationCenterPage);
  }

  handleChatMessage(_message: string) {
    return;
  }

  onQuickAction(_event: { category: string; action: string; initialMessage: string }): void {
    return;
  }

  async onPropertyNameChanged(newName: string): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (!this.selectedPropertyId || !newName) {
      return;
    }
    try {
      await this.addressesService.updateAddress(this.selectedPropertyId, { address_name: newName });
      this.selectedPropertyName = newName;
      this.propertyInfo = { ...this.propertyInfo, name: newName };
      const property = this.addresses?.find(addr => addr.id === this.selectedPropertyId);
      if (property) {
        property.address_name = newName;
      }
      this.util.showSuccess('Property name updated');
    } catch (err: any) {
      this.util.showError(err?.error?.message || 'Failed to update property name');
    }
  }

  async onAccessNotesChanged(newNotes: string): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (!this.selectedPropertyId) {
      return;
    }
    try {
      await this.addressesService.updateAddress(this.selectedPropertyId, { home_access: newNotes });
      this.propertyInfo = { ...this.propertyInfo, accessNotes: newNotes };
      const property = this.addresses?.find(addr => addr.id === this.selectedPropertyId);
      if (property) {
        property.home_access = newNotes;
      }
      this.util.showSuccess('Access notes updated');
    } catch (err: any) {
      this.util.showError(err?.error?.message || 'Failed to update access notes');
    }
  }

  async onCustomFieldsChanged(fields: { label: string; value: string }[]): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (!this.selectedPropertyId) {
      return;
    }
    this.propertyInfo = { ...this.propertyInfo, customFields: fields };
    this.util.showSuccess('Custom fields updated');
  }

  async onPropertyDataChanged(update: PropertyDataUpdate): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (!this.selectedPropertyId) {
      return;
    }
    try {
      await this.addressesService.updateAddress(update, this.selectedPropertyId);
      const property = this.addresses?.find(addr => addr.id === this.selectedPropertyId);
      if (property) {
        if (update.bedrooms !== undefined) { property.bed_count = update.bedrooms; }
        if (update.bathrooms !== undefined) { property.bath_count = update.bathrooms; }
        if (update.living_area_sqft !== undefined) { property.living_area_sqft = update.living_area_sqft; }
        if (update.lot_size_sqft !== undefined) { property.lot_size_sqft = update.lot_size_sqft; }
        if (update.year_built !== undefined) { property.year_built = update.year_built; }
        if (update.property_type !== undefined) { property.property_type = update.property_type; }
      }
      if (this.propertyData) {
        this.propertyData = {
          ...this.propertyData,
          data: {
            ...this.propertyData.data,
            ...update,
          },
          user_submitted: true,
        };
      } else {
        this.propertyData = {
          property: { id: this.selectedPropertyId } as any,
          data: { ...update },
          cached: false,
          fetched_at: new Date().toISOString(),
          expires_at: new Date().toISOString(),
          user_submitted: true,
        };
      }
      this.util.showSuccess('Property details updated');
    } catch (err: any) {
      this.util.showError(err?.error?.message || 'Failed to update property details');
      this.propertyData = this.propertyData ? { ...this.propertyData, _saveError: true } as any : null;
    }
  }

  onPropertyFieldClicked(field: string): void {
    switch (field) {
      case 'gettingStarted':
        // The "Get Started" button now opens the account-manager onboarding flow — the exact
        // same action as the bar above the bottom nav. app.component routes it to the schedule-
        // setup-call step or the "Get Started with TIDY" page depending on onboarding progress.
        this.events.publish('onboarding:open-pill');
        break;
      case 'nextJob':
        this.navigateToJobDetail('next');
        break;
      case 'lastJob':
        this.navigateToJobDetail('last');
        break;
      case 'unpaidBills':
        this.navigateToBillsFiltered();
        break;
      case 'lowInventory':
        this.navigateToInventoryFiltered();
        break;
      case 'listings':
        this.openPropertyListings();
        break;
    }
  }

  private openPropertyListings(): void {
    const params = { addressId: this.selectedPropertyId };
    this.storage.save('dialog-right-side-open', true);
    this.storage.save('dialog-params', params);
    this.rightSidePanelService.setDialogPageTitle('Listings');
    if (this.windowService.isDesktopRes) {
      this.rightSidePanelService.openRightSidePanel(PropertyListingsPage);
    } else {
      this.navCtrl.navigateForward('property-listings');
    }
  }

  onLogJob(): void {
    if (this.demoBlock.block()) { return; }
    this.navCtrl.navigateForward('log-job');
  }

  onAddJob(): void {
    if (this.demoBlock.block()) { return; }
    this.onBookJob();
  }

  async onBookJob(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    this.bookJobService.clearBookJobStorage();
    this.storage.save('bookJobBackPage', 'properties');
    const selectedAddress = this.selectedPropertyId !== null
      ? this.addresses.find(addr => addr.id === this.selectedPropertyId)
      : null;
    const params = {
      address: selectedAddress || this.addresses?.[0],
      bookingType: 'add_job',
    };
    await this.bookService.goToBookJob(params);
    this.rightSidePanelService.afterPanelClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(async () => {
        const didBookJob = localStorage.getItem('didBookJob');
        if (didBookJob === 'true') {
          localStorage.removeItem('didBookJob');
          if (this.selectedPropertyId) {
            this.isLoadingPropertySummary = true;
            this.propertyInfo = {
              ...this.propertyInfo,
              nextJob: 'Loading...',
              lastJob: 'Loading...',
            };
            await this.loadTimelineData(this.selectedPropertyId);
            await this.loadPropertySummary(this.selectedPropertyId);
            this.loadGettingStartedStatus(this.selectedPropertyId);
          }
        }
      });
  }

  onSetupSmartSuggestions(): void {
    this.navCtrl.navigateForward('automation-settings');
  }

  goToSignIn(): void {
    this.authModalService.openAuthModal('auth');
  }

  togglePropertyDropdown() {
    this.showPropertyDropdown = !this.showPropertyDropdown;
  }

  closePropertyDropdown() {
    this.showPropertyDropdown = false;
  }

  selectProperty(addressId: number | null) {
    this.propertyViewMode = 'map';
    this.closeViewModeDropdown();
    this.isPropertyZoomedOut = false;
    this.isPropertyInfoExpanded = true;
    if (this.mapInstance && this.selectedBuildingFeatureIds.length > 0) {
      try {
        this.selectedBuildingFeatureIds.forEach(featureId => {
          this.mapInstance.setFeatureState(
            { source: 'composite', sourceLayer: 'building', id: featureId },
            { selected: false }
          );
        });
        this.selectedBuildingFeatureIds = [];
      } catch (_err) {
        return;
      }
    }
    this.selectedPropertyId = addressId;
    if (addressId !== null) {
      localStorage.setItem('dashboardAddressId', addressId.toString());
      localStorage.setItem('addressId', addressId.toString());
      localStorage.setItem('isAllPropertiesSelected', 'false');
    } else {
      localStorage.removeItem('dashboardAddressId');
      localStorage.removeItem('dashboardAddressName');
      localStorage.removeItem('addressId');
      localStorage.setItem('isAllPropertiesSelected', 'true');
    }
    this.urlStateService.updateQueryParams({ propertyId: addressId });
    this.showEmptyTimeline = false;
    this.updateAddressMarkerOffsets();
    this.removeWeatherEffect();
    if (addressId === null) {
      this.selectedPropertyName = new TranslationPipe().transform('All Properties');
      this.propertyUpdatedSummary = this.getPropertySummary(null);
      this.propertyInfo = {
        name: new TranslationPipe().transform('All Properties'),
        summary: 'Monitoring all properties. Everything is running smoothly across your portfolio.',
        nextJob: 'No upcoming jobs',
        lastJob: 'No past jobs',
        openIssues: 0,
        accessNotes: '',
        unpaidBills: 0,
        lowInventory: 0,
        maintenanceDue: 0,
        customFields: []
      };
      this.locationData = null;
      this.propertyData = null;
      this.pulseEvents = [];
      this.pulseEventsForMobile = [];
      this.isTimelineDataLoaded = false;
      this.zoomToAllProperties();
      this.loadAllPropertiesOpenIssuesCount();
    } else {
      const property = this.addresses?.find(addr => addr.id === addressId);
      this.selectedPropertyName = property?.address_name || property?.address1 || '';
      localStorage.setItem('dashboardAddressName', this.selectedPropertyName);
      this.propertyUpdatedSummary = this.getPropertySummary(property);
      this.isLoadingPropertySummary = true;
      this.nextJobData = null;
      this.lastJobData = null;
      this.nextJobCard = null;
      this.lastJobCard = null;
      this.propertyInfo = {
        name: this.selectedPropertyName,
        summary: 'No recent issues reported. Property is in good standing.',
        nextJob: 'Loading...',
        lastJob: 'Loading...',
        openIssues: 0,
        accessNotes: property?.home_access || '',
        unpaidBills: 0,
        lowInventory: 0,
        maintenanceDue: 0,
        customFields: []
      };
      this.zoomToProperty(property);
      this.fetchLocationAndPropertyData(property);
      this.loadTimelineData(addressId);
      this.loadPropertySummary(addressId);
      this.loadGettingStartedStatus(addressId);
    }
    this.updateMobileHeaderSelectValue();
    this.closePropertyDropdown();
  }

  async selectPropertyById(propertyId: number): Promise<void> {
    const address = this.addresses?.find(a => a.id === propertyId);
    if (!address) {
      this.pendingPropertyId = propertyId;
      await this.loadReports();
      return;
    }
    this.selectProperty(propertyId);
  }

  private async fetchLocationAndPropertyData(property: any): Promise<void> {
    this.locationData = null;
    this.propertyData = null;
    this.propertyCodesData = null;
    this.isLoadingPropertyData = true;
    this.isLoadingLocationData = true;
    this.isLoadingPropertyCodes = true;
    if (property?.zip) {
      try {
        this.locationData = await this.locationDataService.getLocationData(property.zip);
        const weatherCondition = this.mapConditionsToWeatherType(this.locationData?.attributes?.weather?.data?.conditions);
        if (weatherCondition) {
          this.addWeatherEffect(property, weatherCondition);
        }
      } catch (err) {
        // Silently fail - location data is optional
      }
      try {
        this.propertyCodesData = await this.locationDataService.getPropertyCodes(property.zip);
      } catch (err) {
        // Silently fail - property codes are optional
      }
    }
    this.isLoadingLocationData = false;
    this.isLoadingPropertyCodes = false;
    if (property?.address1 && property?.city && property?.zip) {
      try {
        this.propertyData = await this.locationDataService.getPropertyData({
          street_address: property.address1,
          city: property.city,
          state: property.add_state || property.state || '',
          zip: property.zip
        });
      } catch (err) {
        // Silently fail - property data is optional
      }
    }
    this.mergeAddressPropertyData(property);
    this.isLoadingPropertyData = false;
  }

  private mergeAddressPropertyData(property: any): void {
    const userOverrides: Record<string, any> = {};
    if (property?.bed_count) { userOverrides.bedrooms = property.bed_count; }
    if (property?.bath_count) { userOverrides.bathrooms = property.bath_count; }
    if (property?.living_area_sqft) { userOverrides.living_area_sqft = property.living_area_sqft; }
    if (property?.lot_size_sqft) { userOverrides.lot_size_sqft = property.lot_size_sqft; }
    if (property?.year_built) { userOverrides.year_built = property.year_built; }
    if (property?.property_type) { userOverrides.property_type = property.property_type; }
    if (Object.keys(userOverrides).length === 0) {
      return;
    }
    if (this.propertyData) {
      this.propertyData = {
        ...this.propertyData,
        data: { ...this.propertyData.data, ...userOverrides },
      };
    } else {
      this.propertyData = {
        property: { id: property.id } as any,
        data: { ...userOverrides },
        cached: false,
        fetched_at: new Date().toISOString(),
        expires_at: new Date().toISOString(),
      };
    }
  }

  private mapConditionsToWeatherType(conditions: string | undefined): string | null {
    if (!conditions) { return null; }
    const lower = conditions.toLowerCase();
    if (lower.includes('fog') || lower.includes('mist')) { return 'fog'; }
    if (lower.includes('snow') || lower.includes('blizzard') || lower.includes('blowing snow') || lower.includes('ice pellets')) { return 'snow'; }
    if (lower.includes('rain') || lower.includes('drizzle') || lower.includes('shower') || lower.includes('sleet') || lower.includes('freezing rain')) { return 'rain'; }
    if (lower.includes('thunder') || lower.includes('storm')) { return 'rain'; }
    return null;
  }

  onInfoIconClicked(_iconType: string): void {
    // Placeholder for future info icon click handling
  }

  private getPropertySummary(property: any): string {
    if (!property) {
      return 'All Properties Summary: Monitoring all properties. Everything is running smoothly across your portfolio.';
    }
    const address = property.address_name || property.address1 || 'Property';
    return `${address}: No recent issues reported. Property is in good standing. Ready for upcoming tasks.`;
  }

  private isDesktopMode(): boolean {
    return this.windowService.isDesktopRes;
  }

  private getLabelOffset(): number {
    return this.isDesktopMode() ? 120 : 150;
  }

  zoomToProperty(property: any) {
    if (!property) {
      return;
    }
    if (!this.mapInstance) {
      return;
    }
    if (!property.longitude || !property.latitude) {
      return;
    }
    const zoom = this.isDesktopMode() ? 18 : 17;
    const minZoom = zoom - 2;
    const maxZoom = zoom + 3;
    const offset = this.isDesktopMode() ? [0, -120] : [0, -60];
    this.mapInstance.setMinZoom(minZoom);
    this.mapInstance.setMaxZoom(maxZoom);
    this.setupZoomToFloorplanListener(maxZoom);
    try {
      this.mapInstance.flyTo({
        center: [property.longitude, property.latitude],
        zoom,
        pitch: 60,
        bearing: -17.6,
        offset,
        duration: 1500
      });
      const onMoveEnd = () => {
        if (!this.mapInstance) {
          return;
        }
        if (!this.isMapLoaded) {
          this.isMapLoaded = true;
          this.cdr.detectChanges();
        }
        this.mapInstance.off('moveend', onMoveEnd);
        const onIdle = () => {
          if (!this.mapInstance) {
            return;
          }
          this.highlightPropertyBuilding(property);
          this.mapInstance.off('idle', onIdle);
        };
        this.mapInstance.once('idle', onIdle);
      };
      this.mapInstance.on('moveend', onMoveEnd);
    } catch (err) {
      return;
    }
  }

  private zoomToFloorplanListener: () => void;

  private setupZoomToFloorplanListener(maxZoom: number): void {
    if (this.zoomToFloorplanListener && this.mapInstance) {
      this.mapInstance.off('zoom', this.zoomToFloorplanListener);
    }
    const switchToFloorplanZoom = maxZoom - 0.5;
    const switchToMapZoom = maxZoom - 2;
    this.zoomToFloorplanListener = () => {
      if (!this.mapInstance) {
        return;
      }
      const currentZoom = this.mapInstance.getZoom();
      if (this.propertyViewMode === 'map' && currentZoom >= switchToFloorplanZoom) {
        this.propertyViewMode = 'floorplan';
        this.cdr.detectChanges();
      } else if (this.propertyViewMode === 'floorplan' && currentZoom < switchToMapZoom) {
        this.propertyViewMode = 'map';
        this.cdr.detectChanges();
      }
    };
    this.mapInstance.on('zoom', this.zoomToFloorplanListener);
  }

  private removeZoomToFloorplanListener(): void {
    if (this.zoomToFloorplanListener && this.mapInstance) {
      this.mapInstance.off('zoom', this.zoomToFloorplanListener);
      this.zoomToFloorplanListener = null;
    }
  }

  private highlightPropertyBuilding(property: any) {
    if (!property?.longitude || !property?.latitude || !this.mapInstance) {
      return;
    }
    try {
      const pixelPoint = this.mapInstance.project([property.longitude, property.latitude]);
      let buildingFeatures = this.mapInstance.queryRenderedFeatures(pixelPoint, {
        layers: ['building']
      });
      if (buildingFeatures.length === 0) {
        const boxSize = 100;
        const box = [
          [pixelPoint.x - boxSize, pixelPoint.y - boxSize],
          [pixelPoint.x + boxSize, pixelPoint.y + boxSize]
        ];
        buildingFeatures = this.mapInstance.queryRenderedFeatures(box, {
          layers: ['building']
        });
        if (buildingFeatures.length > 0) {
          const closestFeature = this.findClosestBuilding(buildingFeatures, pixelPoint);
          if (closestFeature) {
            buildingFeatures = [closestFeature];
          }
        }
      }
      if (buildingFeatures.length > 0) {
        const seen = new Set();
        const uniqueFeatures = buildingFeatures.filter(f => {
          if (seen.has(f.id)) {
            return false;
          }
          seen.add(f.id);
          return true;
        });
        if (uniqueFeatures.length > 0) {
          this.selectedBuildingFeatureIds = uniqueFeatures.map(f => f.id);
          uniqueFeatures.forEach((feature) => {
            this.mapInstance.setFeatureState(
              { source: 'composite', sourceLayer: 'building', id: feature.id },
              { selected: true }
            );
          });
        }
      }
    } catch (err) {
      return;
    }
  }

  private findClosestBuilding(features: any[], targetPoint: { x: number; y: number }): any {
    let closest = null;
    let minDist = Infinity;
    for (const feature of features) {
      if (feature.geometry?.type === 'Polygon' && feature.geometry.coordinates?.[0]) {
        const coords = feature.geometry.coordinates[0];
        const centroid = this.getPolygonCentroid(coords);
        const centroidPixel = this.mapInstance.project(centroid);
        const dist = Math.sqrt(
          Math.pow(centroidPixel.x - targetPoint.x, 2) +
          Math.pow(centroidPixel.y - targetPoint.y, 2)
        );
        if (dist < minDist) {
          minDist = dist;
          closest = feature;
        }
      }
    }
    return closest;
  }

  private getPolygonCentroid(coords: number[][]): [number, number] {
    let sumX = 0, sumY = 0;
    for (const coord of coords) {
      sumX += coord[0];
      sumY += coord[1];
    }
    return [sumX / coords.length, sumY / coords.length];
  }

  zoomToAllProperties() {
    if (!this.mapInstance) {
      return;
    }
    this.removeZoomToFloorplanListener();
    this.mapInstance.setMinZoom(0);
    this.mapInstance.setMaxZoom(21);
    if (!this.addresses || this.addresses.length === 0) {
      this.mapInstance.setCenter([-118.2437, 34.0522]);
      this.mapInstance.setZoom(10);
      return;
    }
    try {
      const bounds = new mapboxgl.LngLatBounds();
      let validAddressCount = 0;
      this.addresses.forEach((address) => {
        if (address.longitude && address.latitude) {
          bounds.extend([address.longitude, address.latitude]);
          validAddressCount++;
        }
      });
      if (validAddressCount === 0) {
        this.mapInstance.setCenter([-118.2437, 34.0522]);
        this.mapInstance.setZoom(10);
        return;
      }
      setTimeout(() => {
        if (!this.mapInstance) {
          return;
        }
        this.mapInstance.fitBounds(bounds, {
          padding: 50,
          duration: 0,
          maxZoom: 18
        });
        const targetZoom = this.mapInstance.getZoom() - 1;
        this.mapInstance.flyTo({
          zoom: targetZoom,
          duration: 1500
        });
      }, 300);
    } catch (_err) {
      this.mapInstance.setCenter([-118.2437, 34.0522]);
      this.mapInstance.setZoom(10);
    }
  }

  private add3DBuildingsLayer(map: any) {
    if (map.getLayer('3d-buildings')) {
      return;
    }
    const layers = map.getStyle().layers;
    let labelLayerId: string | undefined;
    for (const layer of layers) {
      if (layer.type === 'symbol' && layer.layout['text-field']) {
        labelLayerId = layer.id;
        break;
      }
    }
    const paintConfig = {
      'fill-extrusion-color': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        '#00AABA',
        '#242424'
      ],
      'fill-extrusion-emissive-strength': [
        'case',
        ['boolean', ['feature-state', 'selected'], false],
        0.8,
        0
      ],
      'fill-extrusion-height': [
        'interpolate',
        ['linear'],
        ['zoom'],
        15, 0,
        15.05, ['get', 'height']
      ],
      'fill-extrusion-base': [
        'interpolate',
        ['linear'],
        ['zoom'],
        15, 0,
        15.05, ['get', 'min_height']
      ],
      'fill-extrusion-opacity': 0.6
    };
    map.addLayer(
      {
        'id': '3d-buildings',
        'source': 'composite',
        'source-layer': 'building',
        'filter': ['==', 'extrude', 'true'],
        'type': 'fill-extrusion',
        'minzoom': 15,
        'paint': paintConfig
      },
      labelLayerId
    );
  }

  private setMapLanguage(map: any) {
    const language = localStorage.getItem('language');
    if (!language || language === 'en') {
      return;
    }
    const layers = map.getStyle().layers;
    for (const layer of layers) {
      if (layer.type === 'symbol' && layer.layout?.['text-field']) {
        map.setLayoutProperty(layer.id, 'text-field', [
          'coalesce',
          ['get', `name_${language}`],
          ['get', 'name']
        ]);
      }
    }
  }

  goToSearchPage() {
    this.events.publish('global-search:open');
  }

  goToAddProperty() {
    if (this.demoBlock.block()) { return; }
    this.navCtrl.navigateForward('add-property', { backPage: 'properties' });
    this.closePropertyDropdown();
  }

  private initAddPropertyForm(): void {
    this.addPropertyForm = this.fb.group({
      countryCode: ['US', Validators.required],
      address: ['', Validators.compose([Validators.maxLength(100), Validators.required])],
      address2: ['', Validators.compose([Validators.maxLength(100)])],
      zipcode: ['', this.multipleCountryService.getCountrySettings('US').validator],
      latitude: [''],
      longitude: [''],
      city: [''],
      state: [''],
      addressNickname: ['']
    });
  }

  private resetAddPropertyForm(): void {
    this.addPropertyForm.reset({ countryCode: 'US' });
    this.addressSuggestions = [];
    this.addPropertyError = '';
    this.isAddingProperty = false;
    this.didSelectAddress = false;
    this.addressConfirmed = false;
    this.detectedLocationSummary = '';
  }

  async onAddressSearch(): Promise<void> {
    this.addPropertyError = '';
    this.addressConfirmed = false;
    const addressValue = this.addPropertyForm.get('address')?.value;
    if (!addressValue || addressValue.length < 3) {
      this.addressSuggestions = [];
      return;
    }
    try {
      this.addressSuggestions = await this.addressFormService.updateSearchWorldwide(addressValue);
    } catch (_err) {
      this.addPropertyError = 'Error searching for address. Please try again.';
      this.addressSuggestions = [];
    }
  }

  async selectAddressSuggestion(suggestion: AutoCompleteAddressModel): Promise<void> {
    this.addPropertyForm.patchValue({ address: suggestion.display });
    this.addressSuggestions = [];
    this.didSelectAddress = true;
    try {
      const fullAddress: FullAddressComponentsModel = await this.addressFormService.getFullAddressComponents(suggestion.placeId);
      const latLngData: any = await this.googleGeocode.getLatLong(suggestion.placeId);
      this.addPropertyForm.patchValue({
        countryCode: fullAddress.countryCode || 'US',
        zipcode: fullAddress.postalCode,
        city: fullAddress.city || '',
        state: fullAddress.state || '',
        latitude: latLngData?.latitude || '',
        longitude: latLngData?.longitude || ''
      });
      const validatorObject = this.multipleCountryService.getCountrySettings(fullAddress.countryCode || 'US');
      this.addPropertyForm.get('zipcode')?.setValidators(validatorObject?.validator);
      this.addPropertyForm.get('zipcode')?.updateValueAndValidity();
      this.detectedLocationSummary = this.buildLocationSummary(fullAddress);
      this.addressConfirmed = true;
    } catch (_err) {
      this.addPropertyError = 'Error getting address details. Please try again.';
      this.addressConfirmed = false;
    }
  }

  private buildLocationSummary(address: FullAddressComponentsModel): string {
    const parts = [address.city, address.state, address.countryName].filter(Boolean);
    return parts.join(', ');
  }

  async submitAddProperty(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    this.addPropertyError = '';
    if (!this.didSelectAddress) {
      this.addPropertyError = 'Please select an address from the suggestions.';
      return;
    }
    this.isAddingProperty = true;
    try {
      const formValue = this.addPropertyForm.value;
      const addressArray = formValue.address.split(',');
      const addressStateFallback = addressArray[2]?.trim();
      const coords = await this.googleGeocode.getLatLongWithUnit(
        { address1: addressArray[0], address2: formValue.address2, city: formValue.city, state: formValue.state || addressStateFallback, zip: formValue.zipcode },
        { latitude: formValue.latitude, longitude: formValue.longitude }
      );
      const data: any = {
        country_code: formValue.countryCode,
        add_state: formValue.state || addressStateFallback,
        city: formValue.city,
        address1: addressArray[0],
        address2: formValue.address2,
        zip: formValue.zipcode,
        latitude: coords.latitude,
        longitude: coords.longitude
      };
      if (formValue.addressNickname) {
        data.address_name = formValue.addressNickname;
      }
      const address = await this.addressesService.createAddress(data);
      this.currentAddress.addressId = address.id;
      localStorage.setItem('region_id', address.region_id);
      await this.client.getAddresses(true);
      await this.client.getMoreDetailAddresses(true);
      this.resetAddPropertyForm();
      if (!this.addresses) {
        this.addresses = [];
      }
      this.addresses.push(address);
      this.shouldShowEmptyState = this.addresses?.length === 0;
      this.selectedPropertyId = address.id;
      const addressName = address.address_name || address.address1 || '';
      this.selectedPropertyName = addressName;
      this.propertyInfo.name = addressName;
      await this.loadReports();
      this.selectProperty(address.id);
    } catch (err: any) {
      this.addPropertyError = err?.error?.message || err?.message || 'Failed to add property. Please try again.';
    } finally {
      this.isAddingProperty = false;
    }
  }

  toggleAddPropertyPopup(): void {
    if (this.demoBlock.block()) { return; }
    this.closePropertyDropdown();
    this.rightSidePanelService.openRightSidePanel(AddPropertyFormComponent, {
      smallDisplay: true,
      showImportButton: true,
      onPropertyAdded: async (address: any) => {
        this.currentAddress.addressId = address.id;
        localStorage.setItem('region_id', address.region_id);
        await this.client.getAddresses(true);
        await this.client.getMoreDetailAddresses(true);
        if (!this.addresses) {
          this.addresses = [];
        }
        this.addresses.push(address);
        this.shouldShowEmptyState = this.addresses?.length === 0;
        this.selectedPropertyId = address.id;
        const addressName = address.address_name || address.address1 || '';
        this.selectedPropertyName = addressName;
        this.propertyInfo.name = addressName;
        await this.loadReports();
        this.selectProperty(address.id);
      }
    });
  }


  goToIntegrationInline(): void {
    localStorage.setItem('integrationOptionsBackButton', 'properties');
    this.navCtrl.navigateForward('integration-options');
  }

  toggleViewModeDropdown(): void {
    this.showViewModeDropdown = !this.showViewModeDropdown;
  }

  closeViewModeDropdown(): void {
    this.showViewModeDropdown = false;
  }

  selectViewMode(mode: 'map' | 'floorplan'): void {
    this.propertyViewMode = mode;
    this.closeViewModeDropdown();
  }

  onFloorplanZoomOut(): void {
    console.log('[Dashboard] onFloorplanZoomOut called, switching to map');
    this.propertyViewMode = 'map';
    this.cdr.detectChanges();
    const property = this.addresses?.find(addr => addr.id === this.selectedPropertyId);
    console.log('[Dashboard] property:', property?.address1, 'selectedPropertyId:', this.selectedPropertyId);
    if (property) {
      this.zoomToProperty(property);
    }
  }

  async createFloorplan(): Promise<void> {
    if (this.demoBlock.block()) { return; }
    if (!this.selectedPropertyId) {
      this.util.showError('Please select a property first.');
      return;
    }
    const address = this.addresses?.find(a => a.id === this.selectedPropertyId);
    if (!address) {
      this.util.showError('Could not find the selected property.');
      return;
    }
    const support = await this.roomScanService.isSupported();
    if (!support.supported) {
      this.util.showError(support.reason || 'AR room scanning is not available on this device.');
      return;
    }
    this.navCtrl.navigateForward('ar-room-scan', { address });
  }

  async view3D(): Promise<void> {
    const address = this.addresses?.find(a => a.id === this.selectedPropertyId);
    if (address?.usdz_url) {
      await this.roomScanService.openARQuickLook(address.usdz_url);
    }
  }

  uploadFloorplan(): void {
    if (this.demoBlock.block()) { return; }
    this.util.showSuccess('Opening file picker...');
  }

  zoomOutProperty(): void {
    if (!this.mapInstance || this.isPropertyZoomedOut) {
      return;
    }
    const currentZoom = this.mapInstance.getZoom();
    const targetZoom = Math.max(currentZoom - 4, 14);
    this.mapInstance.flyTo({
      zoom: targetZoom,
      pitch: 45,
      duration: 1000
    });
    this.isPropertyZoomedOut = true;
  }

  zoomInProperty(): void {
    if (!this.mapInstance || !this.isPropertyZoomedOut) {
      return;
    }
    const property = this.addresses?.find(addr => addr.id === this.selectedPropertyId);
    if (property) {
      this.zoomToProperty(property);
    }
    this.isPropertyZoomedOut = false;
  }


  /**
   * Hidden easter egg: 10 clicks on "Dashboard" title opens Capacitor test page
   */
  onDashboardTitleClick() {
    this.dashboardTitleClickCount++;

    // Reset after 3 seconds of inactivity
    clearTimeout(this.dashboardTitleClickTimeout);
    this.dashboardTitleClickTimeout = setTimeout(() => {
      this.dashboardTitleClickCount = 0;
    }, 3000);

    if (this.dashboardTitleClickCount >= 10) {
      this.dashboardTitleClickCount = 0;
    }
  }

  @Loading('', true)
  async sortChanged(sort) {
    try {
      const sortField = this.report?.settings?.fields.find(field => field.field_name === sort.active);
      if (!sortField) {
        return;
      }
      const data = {
        sort_by_field_keys: [{
          field_key: sortField?.field_key,
          sort: sort.direction
        }]
      };
      await this.dashboard.updateDashboardReport(data, this.report.id);
      this.report = await this.dashboard.getDashboardReportData(this.reportId);
      await this.loadTable();
    } catch (err) {
      this.errorMessage = (err.error && err.error.message) ? err.error.message : err.message;
    }
  }

  navigateToPage(page) {
    if (page === 'schedule') {
      const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
      page = shouldShowAllToDosPage || this.windowService.isDesktopRes ? 'schedule' : 'schedule-list';
    }
    if (page === 'rental-demo') {
      return this.util.openUrl('https://www.tidy.com/demo');
    }
    if (page === 'users') {
      return this.goToPage('users', {}, UsersPage);
    }
    if (page === 'to-dos' || page === 'checklists' || page === 'bills' || page === 'inventory' || page === 'restocking') {
      localStorage.setItem('cameFromPage', 'properties');
    }
    this.navCtrl.navigateForward(page);
  }

  async getNudgeCards(nudgeCards) {
    const editAddressDestination = this.report?.data?.rows?.length ? `properties?propertyId=${this.report.data.rows[0][19]}` : 'addresses';
    const shouldShowAllToDosPage = localStorage.getItem('shouldShowAllToDosPage') === 'true';
    const cards = {
      schedule: {
        header: 'Schedule',
        body: 'Schedule a cleaning with a certified Pro in 1 minute.',
        icon: 'assets/img/book-job.svg',
        iconWidth: '25px',
        destination: shouldShowAllToDosPage || this.windowService.isDesktopRes ? 'schedule' : 'schedule-list',
        showCard: true
      },
      to_do_list: {
        header: 'Create a Checklist',
        body: 'Create a digital checklist to share with any Pro. (Free)',
        icon: 'assets/img/to-dos.svg',
        iconWidth: '30px',
        destination: this.windowService.isDesktopRes ? 'checklists' : 'to-dos',
        showCard: true
      },
      existing_pros: {
        header: 'Add Existing Pro',
        body: 'Add an existing Pro you work with to automate requests with them. (Free)',
        icon: 'assets/img/user-red.svg',
        iconWidth: '25px',
        destination: this.isRentalClient && this.windowService.isDesktopRes ? 'contacts' : 'job-request-workflows',
        showCard: true
      },
      map: {
        header: 'Get an Animated Map',
        body: 'Get a free map of your property.',
        icon: 'assets/img/house-sketch.svg',
        iconWidth: '25px',
        destination: editAddressDestination,
        showCard: true
      },
      customer_members: {
        header: 'Users',
        body: 'Add a user so they can log into your account with their own credentials. As the team leader, you can revoke access at any time.',
        icon: 'assets/img/team.svg',
        iconWidth: '25px',
        destination: 'users',
        showCard: true
      }
    };
    const maintenance = {
      header: 'Get a Maintenance Plan',
      body: 'It\'s free! Just scan your home to get a personalized maintenance plan.',
      icon: 'assets/img/tool-box.svg',
      iconWidth: '25px',
      destination: editAddressDestination,
      showCard: true
    };
    const integrations = {
      header: 'Integrate',
      body: 'Connect Airbnb & other channels to automatically book turnovers. (Free)',
      icon: 'assets/img/integrate.svg',
      iconWidth: '40px',
      destination: 'integration/all',
      showCard: true
    };
    const array: any = [];
    const isJobReport = this.report?.settings?.default_date_key === 'jobs.date';
    const hasNoJobItems = !this.report?.data?.rows?.length;
    if (isJobReport && hasNoJobItems) {
      const schedule = {
        header: 'Schedule',
        body: 'Schedule a cleaning with a certified Pro in 1 minute.',
        icon: 'assets/img/book-job.svg',
        iconWidth: '25px',
        destination: shouldShowAllToDosPage || this.windowService.isDesktopRes ? 'schedule' : 'schedule-list',
        showCard: true
      };
      array.push(schedule);
      return array;
    }
    const isMaintenanceReport = this.report?.settings?.default_date_key === 'maintenances.date';
    const hasNoMaintenanceItems = !this.report?.data?.rows?.length;
    const backendSaysShowMaintenance = nudgeCards.includes('maintenance');
    if (isMaintenanceReport && hasNoMaintenanceItems && backendSaysShowMaintenance) {
      array.push(maintenance);
      return array;
    }
    const isReservationReport = this.report?.settings?.default_date_key === 'guest_reservation.check_in_date';
    const hasNoReservationItems = !this.report?.data?.rows?.length;
    const backendSaysShowIntegration = nudgeCards.includes('integrations');
    if (isReservationReport && hasNoReservationItems && backendSaysShowIntegration) {
      array.push(integrations);
      return array;
    }
    nudgeCards.map((card) => {
      if (card !== 'maintenance' && card !== 'integrations') {
        array.push(cards[card]);
      }
    });
    const cameFromMaintenanceSelfBooking = localStorage.getItem('cameFromMaintenanceSelfBooking');
    if (cameFromMaintenanceSelfBooking && backendSaysShowMaintenance) {
      array.unshift(maintenance);
    } else if (backendSaysShowMaintenance) {
      array.push(maintenance);
    }
    const cameFromRentalSelfBooking = localStorage.getItem('cameFromRentalSelfBooking');
    const backendSaysShowIntegrations = nudgeCards.includes('integrations');
    if (cameFromRentalSelfBooking && backendSaysShowIntegrations) {
      array.unshift(integrations);
    } else if (backendSaysShowIntegrations) {
      array.push(integrations);
    }
    if (array?.length > 0) {
      array.push({
        header: 'Get a Demo',
        body: 'The TIDY Concierge can help make sure you have everything set up right.',
        icon: 'assets/img/calendar.svg',
        iconWidth: '30px',
        destination: 'rental-demo',
        showCard: true
      });
    }
    return array;
  }

  goToConciergePage() {
    localStorage.setItem('isAllAddressesSelected', 'true');
    this.goToPage('concierge', {}, ConciergePage);
  }

  goToBillsPage() {
    localStorage.setItem('isAllAddressesSelected', 'true');
    localStorage.setItem('cameFromPage', 'properties');
    this.navCtrl.navigateForward('bills');
  }

  goToMessagesPage() {
    localStorage.setItem('isAllAddressesSelected', 'true');
    const _params = {
      messages: this.messages
    }
    this.navCtrl.navigateForward('messages');
  }

  getMessageDate(date) {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const relative = LuxonDateTime.fromISO(date).setZone(timezone).toRelative();
    if (!relative) {return '';}
    const matches = relative.match(/^(\d+)\s+(\w+)\s+ago$/);
    if (matches) {
      const [_, number, unit] = matches;
      return `${number} ${new TranslationPipe().transform(unit)} ${new TranslationPipe().transform('ago')}`;
    }
    return relative;
  }

  getRelativeDateFromISO(date, addressId) {
    const address = this.addresses?.find((addr) => addr?.id === addressId);
    if (!address) {return '';}
    const relative = LuxonDateTime.fromISO(date, {zone: 'utc'}).setZone(address?.timezone).toRelative();
    if (!relative) {return '';}
    const matches = relative.match(/^(\d+)\s+(\w+)\s+ago$/);
    if (matches) {
      const [_, number, unit] = matches;
      return `${number} ${new TranslationPipe().transform(unit)} ${new TranslationPipe().transform('ago')}`;
    }
    return relative;
  }

  getLoggedIssueAddress(addressId) {
    const address = this.addresses?.find((addr) => addr?.id === addressId);
    return address ? (address?.address1 + (address?.address2 ? (` ${  address?.address2}`) : '')) : '';
  }

  goToLoggedIssuesPage() {
    localStorage.setItem('isAllAddressesSelected', 'true');
    localStorage.setItem('loggedIssuesBackPage', 'properties');
    this.navCtrl.navigateForward('maintenance');
  }

  async goToPage(url, params, component) {
    const isMobileResolution = !this.windowService.isDesktopRes;
    if (!isMobileResolution) {
      const isRightSidePanelAlreadyOpen = await this.storage.retrieve('dialog-right-side-open');
      if (isRightSidePanelAlreadyOpen) {
        this.rightSidePanelService.navigateTo(url, params, component);
      } else {
        this.storage.save('dialog-right-side-open', true);
        this.storage.save('dialog-params', params);
        this.rightSidePanelService.openRightSidePanel(component);
      }
    } else {
      this.navCtrl.navigateForward(url, params);
    }
  }

  pageChange() {
    this.shownRowsCount += 10;
    this.currentRows = this.rows.slice(0, this.shownRowsCount);
  }

  private getRandomWeather(): string | null {
    const weatherTypes = ['rain', 'snow', 'fog', null];
    const randomIndex = Math.floor(Math.random() * weatherTypes.length);
    return weatherTypes[randomIndex];
  }

  private addWeatherEffect(property: any, weatherType: string) {
    if (!this.mapInstance || !property?.longitude || !property?.latitude) {
      return;
    }
    this.removeWeatherEffect();
    const el = document.createElement('div');
    el.className = `weather-overlay ${weatherType}`;
    if (weatherType === 'rain') {
      for (let i = 0; i < 35; i++) {
        const drop = document.createElement('div');
        drop.className = 'raindrop';
        drop.style.left = `${Math.random() * 100}%`;
        drop.style.animationDelay = `${i * 0.017}s`;
        el.appendChild(drop);
      }
    } else if (weatherType === 'snow') {
      for (let i = 0; i < 50; i++) {
        const flake = document.createElement('div');
        flake.className = 'snowflake';
        flake.style.left = `${Math.random() * 100}%`;
        const size = 2.6 + Math.random() * 5.4;
        flake.style.width = `${size}px`;
        flake.style.height = `${size}px`;
        flake.style.animationDelay = `${Math.random() * -8}s`;
        el.appendChild(flake);
      }
    } else if (weatherType === 'fog') {
      for (let i = 0; i < 5; i++) {
        const layer = document.createElement('div');
        layer.className = 'fog-layer';
        layer.style.animationDelay = `${i * 1.6}s`;
        el.appendChild(layer);
      }
    }
    this.weatherMarker = new mapboxgl.Marker(el);
    this.weatherMarker.setLngLat([property.longitude, property.latitude]).addTo(this.mapInstance);
  }

  private removeWeatherEffect() {
    if (this.weatherMarker) {
      this.weatherMarker.remove();
      this.weatherMarker = null;
    }
  }

  ngOnDestroy() {
    this.cleanup();
    clearTimeout(this.dashboardTitleClickTimeout);
    while (this.componentSubscriptions.length) {
      this.componentSubscriptions.pop()?.unsubscribe();
    }
    if (Capacitor.isNativePlatform()) {
      Keyboard.removeAllListeners();
    }
  }

  private focusAddressInput(): void {
    setTimeout(() => {
      this.addressAutoComplete?.focus();
    }, 300);
  }

  cleanup() {
    this.removeWeatherEffect();
    this.addressMarkers.forEach(marker => marker.remove());
    this.addressMarkers = [];
    if (this.mapMoveHandler && this.mapInstance) {
      this.mapInstance.off('moveend', this.mapMoveHandler);
      this.mapMoveHandler = null;
    }
    if (this.mapInstance && typeof this.mapInstance.remove === 'function') {
      this.mapInstance.remove();
      this.mapInstance = null;
    }
    this.dynamicComponentService.destroyAll();
    this.supercluster = null;
  }

  async getInitialChart() {
    const endDate = LuxonDateTime.now();
    const startDate = LuxonDateTime.now().minus({ days: 7 });
    this.chartData = await this.workflowsProvider.getUsageSummary(startDate.toISODate(), endDate.toISODate());
    this.generateChart();
  }

  generateChart(): void {
    this.chartOptions = {
      series: [
        {
          name: new TranslationPipe().transform('Total Workflows Triggered'),
          data: Object.values(this.chartData.breakdown_by_days).map(
            (day) => day.run_count
          ),
        },
        {
          name: new TranslationPipe().transform('Total Automated Tasks'),
          data: Object.values(this.chartData.breakdown_by_days).map(
            (day) => day.action_run_data.automation.count
          ),
        },
        {
          name: new TranslationPipe().transform('Total Concierge Actions'),
          data: Object.values(this.chartData.breakdown_by_days).map(
            (day) => day.action_run_data.concierge.count
          ),
        },
      ],
      chart: {
        type: 'bar',
        height: 274,
        toolbar: {
          show: false
        }
      },
      xaxis: {
        categories: Object.keys(this.chartData.breakdown_by_days).map(
          (dateStr) => new ParseDateTimePipe().transform(dateStr, 'MMM d')
        ),
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        enabled: true,
      },
      yaxis: {
        labels: {
          formatter(val) {
            return Math.round(val)
          }
        }
      }
    };
  }

  async loadTimelineData(addressId: number): Promise<void> {
    this.isLoadingTimelineData = true;
    this.isTimelineDataLoaded = false;
    try {
      const [jobs, issues, maintenances] = await Promise.all([
        this.schedule.getJobs(addressId),
        this.reportIssue.getIssueReportsByAddress(addressId),
        this.genome.getUpcomingMaintenances(addressId)
      ]);
      this.pulseEvents = this.transformToTimelineEvents(jobs || [], issues || [], maintenances || []);
      this.updateMobileEvents();
      this.isTimelineDataLoaded = true;
      this.storage.save(`dashboard_timeline_${addressId}`, this.pulseEvents);
    } catch (error) {
      this.pulseEvents = [];
      this.isTimelineDataLoaded = false;
      console.error('Failed to load timeline data:', error);
    } finally {
      this.isLoadingTimelineData = false;
    }
  }

  private transformToTimelineEvents(jobs: any[], issues: any[], maintenances: any[]): PulseEvent[] {
    const allEvents: { event: PulseEvent; diffDays: number }[] = [];
    const today = LuxonDateTime.now().startOf('day');
    jobs.forEach(job => {
      if (!job.date) {
        return;
      }
      let jobDate = LuxonDateTime.fromISO(job.date).startOf('day');
      if (!jobDate.isValid) {
        jobDate = LuxonDateTime.fromFormat(job.date, 'MMM d, yyyy').startOf('day');
      }
      if (!jobDate.isValid) {
        return;
      }
      const diffDays = Math.floor(jobDate.diff(today, 'days').days);
      allEvents.push({
        event: {
          date: this.formatEventDate(jobDate, today),
          title: this.getJobTitle(job),
          type: this.getEventType(jobDate, today)
        },
        diffDays
      });
    });
    issues.forEach(issue => {
      if (issue.status === 'done' && issue.resolved_at) {
        const resolvedDate = LuxonDateTime.fromISO(issue.resolved_at).startOf('day');
        if (!resolvedDate.isValid) {
          return;
        }
        const diffDays = Math.floor(resolvedDate.diff(today, 'days').days);
        allEvents.push({
          event: {
            date: this.formatEventDate(resolvedDate, today),
            title: `${issue.type_display_name || 'Issue'} resolved`,
            type: this.getEventType(resolvedDate, today)
          },
          diffDays
        });
      } else if (issue.due_date) {
        const dueDate = LuxonDateTime.fromISO(issue.due_date).startOf('day');
        if (!dueDate.isValid) {
          return;
        }
        const diffDays = Math.floor(dueDate.diff(today, 'days').days);
        allEvents.push({
          event: {
            date: this.formatEventDate(dueDate, today),
            title: `${issue.type_display_name || 'Issue'} due`,
            type: this.getEventType(dueDate, today)
          },
          diffDays
        });
      }
    });
    maintenances.forEach(maintenance => {
      if (maintenance.due_date) {
        const dueDate = LuxonDateTime.fromISO(maintenance.due_date).startOf('day');
        if (!dueDate.isValid) {
          return;
        }
        const diffDays = Math.floor(dueDate.diff(today, 'days').days);
        allEvents.push({
          event: {
            date: this.formatEventDate(dueDate, today),
            title: `${maintenance.room_object?.name || 'Maintenance'} due`,
            type: this.getEventType(dueDate, today)
          },
          diffDays
        });
      }
    });
    const pastEvents = allEvents
      .filter(e => e.diffDays < 0)
      .sort((a, b) => b.diffDays - a.diffDays)
      .slice(0, 5);
    const todayEvents = allEvents.filter(e => e.diffDays === 0);
    const futureEvents = allEvents
      .filter(e => e.diffDays > 0)
      .sort((a, b) => a.diffDays - b.diffDays)
      .slice(0, 5);
    const limitedEvents = [...pastEvents, ...todayEvents, ...futureEvents]
      .sort((a, b) => a.diffDays - b.diffDays)
      .map(e => e.event);
    return limitedEvents;
  }

  private formatEventDate(eventDate: LuxonDateTime, today: LuxonDateTime): string {
    const diffDays = Math.floor(eventDate.diff(today, 'days').days);
    if (isNaN(diffDays)) {
      return 'Unknown';
    }
    if (diffDays === 0) {return 'Today';}
    if (diffDays === -1) {return 'Yesterday';}
    if (diffDays === 1) {return 'Tomorrow';}
    if (diffDays < 0) {return `${Math.abs(diffDays)} days ago`;}
    if (diffDays <= 7) {return `In ${diffDays} days`;}
    if (diffDays <= 14) {return 'In 1 week';}
    if (diffDays <= 21) {return 'In 2 weeks';}
    if (diffDays <= 30) {return 'In 3 weeks';}
    return `In ${Math.floor(diffDays / 7)} weeks`;
  }

  private getEventType(eventDate: LuxonDateTime, today: LuxonDateTime): 'past' | 'today' | 'future' {
    const diffDays = Math.floor(eventDate.diff(today, 'days').days);
    if (diffDays === 0) {return 'today';}
    if (diffDays < 0) {return 'past';}
    return 'future';
  }

  private getJobTitle(job: any): string {
    return job.service_name || job.service_type_key || job.service_category || 'Cleaning';
  }

  private getEventSortValue(event: PulseEvent): number {
    const mapping: { [key: string]: number } = {
      'Yesterday': -1,
      'Today': 0,
      'Tomorrow': 1
    };
    if (mapping[event.date] !== undefined) {return mapping[event.date];}
    if (event.date.includes('days ago')) {
      return -parseInt(event.date);
    }
    if (event.date.includes('In ')) {
      const match = event.date.match(/In (\d+)/);
      if (match) {return parseInt(match[1]);}
      if (event.date.includes('week')) {
        const weeks = parseInt(event.date.match(/(\d+)/)?.[1] || '1');
        return weeks * 7;
      }
    }
    return 100;
  }

  private updateMobileEvents(): void {
    const lastPast = this.pulseEvents.filter(e => e.type === 'past').pop();
    const todayEvent = this.pulseEvents.find(e => e.type === 'today');
    const firstFuture = this.pulseEvents.find(e => e.type === 'future');
    this.pulseEventsForMobile = [lastPast, todayEvent, firstFuture].filter(e => e !== undefined);
  }

  private async loadPropertySummary(addressId: number): Promise<void> {
    try {
      const [addressData, issueReports, inventoryResp] = await Promise.all([
        this.addressesService.getAddressWithSummary(addressId),
        this.reportIssue.getIssueReportsByAddress(addressId),
        this.genome.getInventoryRoomObjects({page: 1, per_page: 100, address_id: addressId})
      ]);
      const openStatuses = ['reported', 'approved', 'in_progress'];
      const openIssuesCount = issueReports.filter((issue: any) => openStatuses.includes(issue.status) && !issue.archived_at).length;
      const lowInventoryCount = (inventoryResp?.body || []).filter((item: any) => item.consumable && item.current_quantity < item.min_quantity).length;
      const nextJob = addressData?.next_job;
      if (nextJob) {
        setTimeout(() => {
          this.oneSignalNotification.requestPermissionIfNeeded();
        }, 2000);
      }
      const lastJob = addressData?.last_job;
      this.nextJobData = nextJob || null;
      this.lastJobData = lastJob || null;
      const jobIds = [nextJob?.id, lastJob?.id].filter(id => id !== null && id !== undefined);
      let jobCards: any[] = [];
      if (jobIds.length > 0) {
        jobCards = await this.schedule.getJobCards(addressId, jobIds);
      }
      this.nextJobCard = this.schedule.findJobCard(jobCards, nextJob?.id);
      this.lastJobCard = this.schedule.findJobCard(jobCards, lastJob?.id);
      const nextJobCard = this.nextJobCard;
      const lastJobCard = this.lastJobCard;
      const nextJobText = nextJob
        ? `${nextJobCard?.template_data?.job?.type?.name || 'Cleaning'} - ${new ParseDateTimePipe().transform(nextJob.date, 'MMM d')}`
        : 'No upcoming jobs';
      const lastJobText = lastJob
        ? `${lastJobCard?.template_data?.job?.type?.name || 'Cleaning'} - ${new ParseDateTimePipe().transform(lastJob.date, 'MMM d')}`
        : 'No past jobs';
      const propertyName = addressData?.address_name || '';
      // The address's real connected listings — count + platform labels for the LISTINGS card.
      const listings: AddressListing[] = await this.client.getAddressListings(addressId).catch(() => []);
      this.propertyInfo = {
        ...this.propertyInfo,
        name: propertyName,
        nextJob: nextJobText,
        lastJob: lastJobText,
        openIssues: openIssuesCount,
        unpaidBills: addressData?.unpaid_invoices_count || 0,
        lowInventory: lowInventoryCount,
        maintenanceDue: addressData?.maintenance_due_next_month_count || 0,
        listingsCount: listings.length,
        listingsPlatforms: listings.map(listing => formatPlatformName(listing.platform))
      };
      if (propertyName) {
        this.selectedPropertyName = propertyName;
      }
      this.storage.save(`dashboard_propertyInfo_${addressId}`, this.propertyInfo);
    } catch (error) {
      this.nextJobData = null;
      this.lastJobData = null;
      this.nextJobCard = null;
      this.lastJobCard = null;
      this.propertyInfo = {
        ...this.propertyInfo,
        nextJob: 'No upcoming jobs',
        lastJob: 'No past jobs',
      };
    } finally {
      this.isLoadingPropertySummary = false;
    }
  }

  private async loadGettingStartedStatus(addressId: number): Promise<void> {
    if (!this.isRentalClient) {return;}
    try {
      const steps = ['onboarding-call', 'bank', 'locks', 'legal', 'pros', 'listing-opt', 'pricing', 'checklists', 'guidebook', 'listings'];
      const prefix = `external.onboarding_step.${addressId}.`;
      const keys = [...steps.map(s => `${prefix}${s}`), `${prefix}go_live`, `${prefix}notify`];
      const metadata: any[] = await this.client.getMetadata(keys);
      const metaMap = new Map<string, any>();
      if (Array.isArray(metadata)) {
        for (const item of metadata) {
          if (item?.key) {
            const stripped = item.key.replace(/^external\./, '');
            metaMap.set(stripped, item.value);
          }
        }
      }
      const goLiveVal = metaMap.get(`onboarding_step.${addressId}.go_live`);
      if (goLiveVal === 'completed') {
        this.propertyInfo = { ...this.propertyInfo, showGettingStarted: false, gettingStartedLoaded: true };
        return;
      }
      const completedSteps = new Set<string>();
      for (const step of steps) {
        if (metaMap.get(`onboarding_step.${addressId}.${step}`) === 'completed') {
          completedSteps.add(step);
        }
      }
      if (metaMap.get(`onboarding_step.${addressId}.notify`) !== 'completed') {
        completedSteps.delete('pros');
      }
      this.propertyInfo = {
        ...this.propertyInfo,
        gettingStartedCompletedSteps: completedSteps.size,
        gettingStartedTotalSteps: steps.length,
        showGettingStarted: true,
        gettingStartedLoaded: true
      };
    } catch (err) {
      this.propertyInfo = { ...this.propertyInfo, gettingStartedLoaded: true };
    }
  }

  private async loadAllPropertiesOpenIssuesCount(): Promise<void> {
    try {
      const issueReports = await this.reportIssue.getIssueReports();
      const openStatuses = ['reported', 'approved', 'in_progress'];
      const openIssuesCount = issueReports.filter((issue: any) => openStatuses.includes(issue.status) && !issue.archived_at).length;
      this.propertyInfo = {
        ...this.propertyInfo,
        openIssues: openIssuesCount
      };
    } catch (error) {
      // Silently fail, keep showing 0
    }
  }

  private async restoreCachedDashboardData(): Promise<void> {
    const savedAddressId = localStorage.getItem('dashboardAddressId');
    if (!savedAddressId) {
      return;
    }
    const addressId = Number(savedAddressId);
    if (isNaN(addressId)) {
      return;
    }
    const [cachedPropertyInfo, cachedTimeline] = await Promise.all([
      this.storage.retrieve(`dashboard_propertyInfo_${addressId}`),
      this.storage.retrieve(`dashboard_timeline_${addressId}`)
    ]);
    if (cachedPropertyInfo) {
      // The real listings count/platforms are cached by loadPropertySummary; the live load
      // below refreshes them.
      this.propertyInfo = cachedPropertyInfo;
      this.isLoadingPropertySummary = false;
    }
    if (cachedTimeline) {
      this.pulseEvents = cachedTimeline;
      this.updateMobileEvents();
      this.isTimelineDataLoaded = true;
    }
  }

  private async navigateToJobDetail(type: 'next' | 'last'): Promise<void> {
    const jobData = type === 'next' ? this.nextJobData : this.lastJobData;
    if (!jobData?.id) {return;}

    const addressId = this.selectedPropertyId;
    if (type === 'next') {
      localStorage.setItem('jobBackPage', 'properties');
      const card = this.nextJobCard;
      const bookingId = card?.template_data?.booking?.id;
      const params = { jobId: jobData.id, addressId, card, job: card?.template_data?.job, bookingId };
      await this.storage.save('dialog-params', params);
      await this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(JobPage);
    } else {
      localStorage.setItem('pastJobBackPage', 'properties');
      if (jobData.is_private) {
        this.rightSidePanelService.navigateTo(
          `past-private-job/${jobData.id}`,
          { jobId: jobData.id, addressId },
          PastPrivateJobPage
        );
      } else {
        this.rightSidePanelService.navigateTo(
          `past-job/${jobData.id}`,
          { jobId: jobData.id, addressId },
          PastJobPage
        );
      }
    }
  }

  private navigateToMaintenanceFiltered(): void {
    if (this.selectedPropertyId) {
      localStorage.setItem('maintenanceAddressId', this.selectedPropertyId.toString());
      localStorage.setItem('isAllAddressesSelected', 'false');
    } else {
      localStorage.setItem('isAllAddressesSelected', 'true');
    }
    localStorage.setItem('loggedIssuesBackPage', 'properties');
    this.navCtrl.navigateForward('maintenance');
  }

  private navigateToBillsFiltered(): void {
    if (this.selectedPropertyId) {
      localStorage.setItem('billsAddressFilter', JSON.stringify([this.selectedPropertyId]));
    }
    localStorage.setItem('cameFromPage', 'properties');
    this.navCtrl.navigateForward('bills');
  }

  private navigateToInventoryFiltered(): void {
    if (this.selectedPropertyId) {
      localStorage.setItem('inventoryPropertyFilter', this.selectedPropertyId.toString());
    }
    localStorage.setItem('inventoryLowOnly', 'true');
    localStorage.setItem('cameFromPage', 'properties');
    this.navCtrl.navigateForward('restocking');
  }

  goToEditPropertyPage() {
    const params = {
      addressId: this.selectedPropertyId,
    };
    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.storage.save('dialog-params', params);
      this.rightSidePanelService.openRightSidePanel(EditPropertyPage);
      this.rightSidePanelService.panelClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
        const didJustDeleteProperty = await this.storage.retrieve('didJustDeleteProperty');
        if (didJustDeleteProperty) {
          localStorage.removeItem('didJustDeleteProperty');
          localStorage.removeItem('dashboardAddressId');
          localStorage.removeItem('dashboardAddressName');
          this.selectedPropertyId = null;
          this.selectedPropertyName = new TranslationPipe().transform('All Properties');
          this.mobileHeaderSelectValue = 'null';
          this.loadPage(true, false);
        }
      });
    } else {
      this.navCtrl.navigateForward(`edit-property/${this.selectedPropertyId}`, params);
    }
  }

}

interface chartDataInterface {
  total_run_count: any;
  total_action_run_count: any;
  breakdown_by_days: dayBreakdownInterface;
}

interface dayBreakdownInterface {
  [date: string]: {
    run_count: number;
    action_run_data: {
      cost: number;
      count: number;
      automation: {
        cost: number;
        count: number;
      },
      concierge: {
        cost: number;
        count: number;
      },
      free: {
        cost: number;
        count: number;
      }
    }
  }
}
